import { useState, useMemo, useEffect, useRef } from 'react';
import { Upload, Music, Clock, Headphones, TrendingUp, Calendar, Award, Zap, Play, BarChart3, Search, Sunrise, Sunset, Moon, Sun, MapPin, Smartphone, Trophy, Users, Sparkles, LogIn, LogOut, UserCircle, PlayCircle, ExternalLink } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { loginToSpotify, logout, handleCallback } from './spotifyAuth';
import { fetchUserListeningData, transformSpotifyData, createPlaylist, addTracksToPlaylist, getCurrentUser } from './spotifyAPI';
import { track } from '@vercel/analytics';

function App() {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'artists' | 'tracks' | 'insights' | 'timeline' | 'patterns' | 'advanced'>('overview');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [, setIsSpotifyMode] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState<any>(null);
  const [spotifyData, setSpotifyData] = useState<any>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);

  // Audio preview functions
  const playPreview = (previewUrl: string, trackId: string) => {
    if (currentlyPlayingId === trackId) {
      // Stop playing
      audioElement?.pause();
      setCurrentlyPlayingId(null);
    } else {
      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
      
      // Create new audio element if needed
      const audio = new Audio(previewUrl);
      audio.addEventListener('ended', () => setCurrentlyPlayingId(null));
      audio.play();
      setAudioElement(audio);
      setCurrentlyPlayingId(trackId);
    }
  };

  // Auto-focus search input when switching to artists or tracks tab
  useEffect(() => {
    if ((activeTab === 'artists' || activeTab === 'tracks') && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [activeTab]);

  // Track tab changes as virtual pageviews only when results are shown
  useEffect(() => {
    if (!summary) return;
    const path = `/tab/${activeTab}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, document.title, path);
    }
    track('pageview', { url: path });
  }, [activeTab, summary]);

  // Ensure initial (no results) view uses /home
  useEffect(() => {
    if (!summary) {
      const homePath = '/home';
      if (window.location.pathname !== homePath) {
        window.history.replaceState({}, document.title, homePath);
      }
    }
  }, [summary]);

  // Sync state with URL on initial load and browser navigation
  useEffect(() => {
    const allowedTabs = ['overview', 'artists', 'tracks', 'timeline', 'patterns', 'insights', 'advanced'] as const;
    const updateFromPath = () => {
      const match = window.location.pathname.match(/^\/tab\/(overview|artists|tracks|timeline|patterns|insights|advanced)$/);
      if (match) {
        setActiveTab(match[1] as typeof allowedTabs[number]);
        return;
      }
      // If navigating to /home, clear results to show UploadView
      if (window.location.pathname === '/home') {
        setSummary(null);
      }
    };
    updateFromPath();
    window.addEventListener('popstate', updateFromPath);
    return () => window.removeEventListener('popstate', updateFromPath);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!summary || e.ctrlKey || e.metaKey || e.altKey) return;
      
      // Check if user is already typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      
      const tabs = ['overview', 'artists', 'tracks', 'timeline', 'patterns', 'insights', 'advanced'] as const;
      const currentIndex = tabs.indexOf(activeTab);
      
      // Handle arrow key navigation
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % tabs.length;
        setActiveTab(tabs[nextIndex]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        setActiveTab(tabs[prevIndex]);
      } 
      // Handle alphanumeric keys and backspace/delete - focus search input
      else if ((activeTab === 'artists' || activeTab === 'tracks') && 
               (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key) || 
                e.key === 'Backspace' || e.key === 'Delete')) {
        // Focus the search input and let the key be typed/deleted
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          // The character/action will automatically be performed in the now-focused input
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeTab, summary]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.currentTarget.value);
  };

  // Debounce only the search trigger, not the input value
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (searchQuery.length === 0) {
      setDebouncedSearch('');
      setIsSearching(false);
      return;
    }
    if (searchQuery.length < 2) {
      setDebouncedSearch('');
      setIsSearching(false);
      return;
    }
    // Only set searching state after a delay to avoid re-renders on every keystroke
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setIsSearching(false);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Handle Spotify OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      setError(`Spotify auth error: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && state) {
      handleCallback(code, state)
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
          loadSpotifyData();
        })
        .catch((err) => {
          setError(`Authentication failed: ${err.message}`);
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    }
  }, []);

  // Load Spotify data
  const loadSpotifyData = async () => {
    try {
      setIsProcessing(true);
      setProcessingProgress(20);
      
      const user = await getCurrentUser();
      setSpotifyUser(user);
      setProcessingProgress(40);
      
      const data = await fetchUserListeningData();
      setSpotifyData(data);
      setProcessingProgress(60);
      
      const transformed = transformSpotifyData(data);
      setProcessingProgress(80);
      
      // Set the summary to display the data
      setSummary(transformed);
      setProcessingProgress(100);
      setIsSpotifyMode(true);
      setIsProcessing(false);
    } catch (err: any) {
      setError(`Failed to load Spotify data: ${err.message}`);
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  };

  // Handle Spotify login
  const handleSpotifyLogin = () => {
    loginToSpotify();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setSpotifyUser(null);
    setSpotifyData(null);
    setSummary(null);
    setIsSpotifyMode(false);
  };

  // Toggle follow artist (ready for UI integration)
  // const toggleFollowArtist = async (artistId: string) => {
  //   try {
  //     const isFollowing = followingStatus[artistId];
  //     if (isFollowing) {
  //       await unfollowArtist(artistId);
  //     } else {
  //       await followArtist(artistId);
  //     }
  //     setFollowingStatus(prev => ({ ...prev, [artistId]: !isFollowing }));
  //   } catch (err: any) {
  //     setError(`Failed to ${followingStatus[artistId] ? 'unfollow' : 'follow'} artist: ${err.message}`);
  //   }
  // };

  // Play audio preview (ready for UI integration)
  // const playPreview = (url: string) => {
  //   if (audioElement) {
  //     audioElement.pause();
  //   }
  //   
  //   if (currentlyPlaying === url) {
  //     setCurrentlyPlaying(null);
  //     setAudioElement(null);
  //     return;
  //   }
  //   
  //   const audio = new Audio(url);
  //   audio.volume = 0.5;
  //   audio.play();
  //   audio.onended = () => {
  //     setCurrentlyPlaying(null);
  //     setAudioElement(null);
  //   };
  //   setAudioElement(audio);
  //   setCurrentlyPlaying(url);
  // };

  // Stop audio
  const stopAudio = () => {
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
    }
  };

  // Create playlist from top tracks
  const createTopTracksPlaylist = async () => {
    if (!spotifyUser || !spotifyData) return;
    
    try {
      const playlistName = `My Wrapped ${new Date().getFullYear()}`;
      const description = `My top tracks generated by Music Wrapped on ${new Date().toLocaleDateString()}`;
      
      const playlist = await createPlaylist(spotifyUser.id, playlistName, description, true);
      
      const trackUris = spotifyData.topTracks.longTerm.slice(0, 50).map((track: any) => track.uri);
      await addTracksToPlaylist(playlist.id, trackUris);
      
      alert(`✅ Playlist "${playlistName}" created successfully! Check your Spotify app.`);
    } catch (err: any) {
      setError(`Failed to create playlist: ${err.message}`);
    }
  };

  // Helper: Extract top artists, tracks, and stats from Spotify JSON
  function generateWrappedSummary(data: any) {
    if (!Array.isArray(data)) return null;
    const trackCount: Record<string, number> = {};
    const artistCount: Record<string, number> = {};
    const albumCount: Record<string, number> = {};
    const platformCount: Record<string, number> = {};
    const countryCount: Record<string, number> = {};
    const skipCount: Record<string, number> = {};
    const dayCount: Record<string, number> = {};
    const hourCount: Record<string, number> = {};
    const trackPlayCount: Record<string, number> = {};
    const artistDayCount: Record<string, Record<string, number>> = {};
    let totalMs = 0;
    let totalTrackPlays = 0;
    let totalTrackLength = 0;
    const uniqueArtists = new Set();
    let firstTrack = null;
    let lastTrack = null;
    let firstDate: string | null = null;
    let lastDate: string | null = null;
    let longestTrack = { name: '', ms: 0 };
    let shortestTrack = { name: '', ms: Infinity };
    let prevTs: Date | null = null;
    let sessionThreshold = 1000 * 60 * 30;
    let sessionStart: Date | null = null;
    let sessionDuration = 0;
    let sessionMax = 0;
    let sessionStartMax: Date | null = null;
    let sessionEndMax: Date | null = null;
    let uniqueTracks = new Set();
    let singlePlayTracks = new Set();
    const reasonEndCount: Record<string, number> = {};
    const dayOfWeekCount: Record<string, number> = {};
    const timeOfDayCount = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    const monthlyData: Record<string, { tracks: number, hours: number, newArtists: Set<string>, newTracks: Set<string> }> = {};
    const dailyHours: Record<string, number> = {};
    const seenArtistsGlobal = new Set<string>();
    const seenTracksGlobal = new Set<string>();

    const sortedData = [...data].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

    // Extract date range from data
    const dateFrom = sortedData.length > 0 ? sortedData[0].ts : null;
    const dateTo = sortedData.length > 0 ? sortedData[sortedData.length - 1].ts : null;
    const formattedFromDate = dateFrom ? new Date(dateFrom).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
    const formattedToDate = dateTo ? new Date(dateTo).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

    sortedData.forEach((entry: any) => {
      const track = entry.master_metadata_track_name || 'Unknown';
      const artist = entry.master_metadata_album_artist_name || 'Unknown';
      const album = entry.master_metadata_album_album_name || 'Unknown';
      const ms = entry.ms_played || 0;
      const skipped = entry.skipped || false;
      const ts = entry.ts || null;
      const platform = entry.platform || 'Unknown';
      const country = entry.conn_country || 'Unknown';
      const reasonEnd = entry.reason_end || 'Unknown';
      if (track !== 'Unknown') {
        trackCount[track] = (trackCount[track] || 0) + ms;
        trackPlayCount[track] = (trackPlayCount[track] || 0) + 1;
        uniqueTracks.add(track);
      }
      if (artist !== 'Unknown') {
        artistCount[artist] = (artistCount[artist] || 0) + ms;
        uniqueArtists.add(artist);
        // Artist per day
        if (ts) {
          const day = ts.split('T')[0];
          if (!artistDayCount[artist]) artistDayCount[artist] = {};
          artistDayCount[artist][day] = (artistDayCount[artist][day] || 0) + 1;
        }
      }
      if (album !== 'Unknown') {
        albumCount[album] = (albumCount[album] || 0) + ms;
      }
      if (platform !== 'Unknown') {
        platformCount[platform] = (platformCount[platform] || 0) + 1;
      }
      if (country !== 'Unknown') {
        countryCount[country] = (countryCount[country] || 0) + 1;
      }
      if (skipped && track !== 'Unknown') {
        skipCount[track] = (skipCount[track] || 0) + 1;
      }
      if (reasonEnd !== 'Unknown') {
        reasonEndCount[reasonEnd] = (reasonEndCount[reasonEnd] || 0) + 1;
      }
      if (ts) {
        const day = ts.split('T')[0];
        dayCount[day] = (dayCount[day] || 0) + 1;
        const hour = new Date(ts).getHours();
        hourCount[hour] = (hourCount[hour] || 0) + 1;
        
        // Day of week analysis
        const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(ts).getDay()];
        dayOfWeekCount[dayOfWeek] = (dayOfWeekCount[dayOfWeek] || 0) + 1;
        
        // Time of day analysis
        if (hour >= 5 && hour < 12) timeOfDayCount.morning++;
        else if (hour >= 12 && hour < 17) timeOfDayCount.afternoon++;
        else if (hour >= 17 && hour < 22) timeOfDayCount.evening++;
        else timeOfDayCount.night++;
        
        // Monthly trends
        const month = ts.substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = { tracks: 0, hours: 0, newArtists: new Set(), newTracks: new Set() };
        }
        monthlyData[month].tracks++;
        monthlyData[month].hours += ms / 1000 / 60 / 60;
        if (!seenArtistsGlobal.has(artist)) {
          monthlyData[month].newArtists.add(artist);
          seenArtistsGlobal.add(artist);
        }
        if (!seenTracksGlobal.has(track)) {
          monthlyData[month].newTracks.add(track);
          seenTracksGlobal.add(track);
        }
        
        // Daily hours for timeline
        dailyHours[day] = (dailyHours[day] || 0) + (ms / 1000 / 60 / 60);
        // Track first and last
        if (!firstDate || ts < firstDate) {
          firstDate = ts;
          firstTrack = { track, artist, ts };
        }
        if (!lastDate || ts > lastDate) {
          lastDate = ts;
          lastTrack = { track, artist, ts };
        }
        // Session analysis
        const currentTs = new Date(ts);
        if (!sessionStart) {
          sessionStart = currentTs;
          sessionDuration = ms;
        } else if (prevTs && currentTs.getTime() - prevTs.getTime() < sessionThreshold) {
          sessionDuration += ms;
        } else {
          // New session
          if (sessionDuration > sessionMax) {
            sessionMax = sessionDuration;
            sessionStartMax = sessionStart;
            sessionEndMax = prevTs;
          }
          sessionStart = currentTs;
          sessionDuration = ms;
        }
        prevTs = currentTs;
      }
      totalMs += ms;
      if (ms > 0) {
        totalTrackPlays++;
        totalTrackLength += ms;
        if (ms > longestTrack.ms) longestTrack = { name: track, ms };
        if (ms < shortestTrack.ms && ms > 0) shortestTrack = { name: track, ms };
      }
    });

    // Final session check
    if (sessionDuration > sessionMax) {
      sessionMax = sessionDuration;
      sessionStartMax = sessionStart;
      sessionEndMax = prevTs;
    }

    // All tracks, artists, and albums (sorted by play time)
    const allArtistsList = Object.entries(artistCount)
      .sort((a, b) => b[1] - a[1])
      .map(([name, ms]) => ({ name, ms }));
    const allTracksList = Object.entries(trackCount)
      .sort((a, b) => b[1] - a[1])
      .map(([name, ms]) => ({ name, ms }));
    const allAlbumsList = Object.entries(albumCount)
      .sort((a, b) => b[1] - a[1])
      .map(([name, ms]) => ({ name, ms }));
    
    // Top 10 tracks, artists, and albums
    const topTracks = allTracksList.slice(0, 10);
    const topArtists = allArtistsList.slice(0, 10);
    const topAlbums = allAlbumsList.slice(0, 10);
    const topPlatforms = Object.entries(platformCount)
      .sort((a, b) => b[1] - a[1])[0];
    const topCountries = Object.entries(countryCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Most skipped track
    const mostSkipped = Object.entries(skipCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Most played day
    const mostPlayedDay = Object.entries(dayCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Most played track by count
    const mostPlayedTrack = Object.entries(trackPlayCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Most common hour
    const mostCommonHour = Object.entries(hourCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Most common reason end
    const mostCommonReasonEnd = Object.entries(reasonEndCount)
      .sort((a, b) => b[1] - a[1])[0];
    // Tracks played only once
    Object.entries(trackPlayCount).forEach(([track, count]) => {
      if (count === 1) singlePlayTracks.add(track);
    });
    // Most repeated artist in a day
    let mostRepeatedArtistDay = { artist: '', day: '', count: 0 };
    Object.entries(artistDayCount).forEach(([artist, days]) => {
      Object.entries(days).forEach(([day, count]) => {
        if (count > mostRepeatedArtistDay.count) {
          mostRepeatedArtistDay = { artist, day, count };
        }
      });
    });
    const allDays = Object.keys(dayCount).sort();
    let maxStreak = 0, currentStreak = 0, prevDay: string | null = null;
    for (let i = 0; i < allDays.length; i++) {
      if (prevDay) {
        const prev = new Date(prevDay);
        const curr = new Date(allDays[i]);
        if ((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24) === 1) {
          currentStreak++;
        } else {
          currentStreak = 1;
        }
      } else {
        currentStreak = 1;
      }
      if (currentStreak > maxStreak) maxStreak = currentStreak;
      prevDay = allDays[i];
    }

    // Skipped/completed percentage
    const totalSkips = Object.values(skipCount).reduce((a, b) => a + b, 0);
    const skipPercent = totalTrackPlays ? ((totalSkips / totalTrackPlays) * 100).toFixed(1) : '0';
    const completePercent = totalTrackPlays ? (100 - parseFloat(skipPercent)).toFixed(1) : '0';
    
    // Weekend vs Weekday
    const weekendDays = ['Saturday', 'Sunday'];
    const weekendTracks = weekendDays.reduce((sum, day) => sum + (dayOfWeekCount[day] || 0), 0);
    const weekdayTracks = totalTrackPlays - weekendTracks;
    
    // Time of day persona
    const maxTimeOfDay = Object.entries(timeOfDayCount).sort((a, b) => b[1] - a[1])[0];
    const timePersona = maxTimeOfDay ? maxTimeOfDay[0] : 'balanced';
    
    // Discovery rate
    const monthlyDiscovery = Object.entries(monthlyData).map(([month, data]) => ({
      month,
      newArtists: data.newArtists.size,
      newTracks: data.newTracks.size,
      tracks: data.tracks,
      hours: parseFloat(data.hours.toFixed(2))
    })).sort((a, b) => {
      // Properly sort by year-month (format: YYYY-MM)
      const [yearA, monthA] = a.month.split('-').map(Number);
      const [yearB, monthB] = b.month.split('-').map(Number);
      return yearA !== yearB ? yearA - yearB : monthA - monthB;
    });
    
    // Binge detection (artist played 10+ times in one day)
    const binges: Array<{artist: string, day: string, count: number}> = [];
    Object.entries(artistDayCount).forEach(([artist, days]) => {
      Object.entries(days).forEach(([day, count]) => {
        if (count >= 10) binges.push({ artist, day, count });
      });
    });
    binges.sort((a, b) => b.count - a.count);
    
    // Listening velocity
    const avgTracksPerDay = allDays.length > 0 ? (totalTrackPlays / allDays.length).toFixed(2) : '0';
    const avgHoursPerDay = allDays.length > 0 ? ((totalMs / 1000 / 60 / 60) / allDays.length).toFixed(2) : '0';
    
    // Platform breakdown
    const allPlatforms = Object.entries(platformCount).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    
    // Country breakdown
    const allCountries = Object.entries(countryCount).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    
    // Hourly listening pattern
    const hourlyPattern = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      count: hourCount[i] || 0
    }));
    
    // Day of week pattern
    const dayOfWeekPattern = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => ({
      day,
      count: dayOfWeekCount[day] || 0
    }));

    // NEW FEATURES

    // 1. Achievements/Badges
    const badges = [];
    if (timeOfDayCount.night >= 50) badges.push({ name: 'Night Owl', icon: '🦉', description: '50+ plays between 2-6 AM' });
    if (uniqueArtists.size >= 100) badges.push({ name: 'Explorer', icon: '🗺️', description: '100+ unique artists' });
    const topArtistPercent = allArtistsList.length > 0 ? (allArtistsList[0].ms / totalMs) * 100 : 0;
    if (topArtistPercent > 20) badges.push({ name: 'Loyalist', icon: '👑', description: 'One artist is >20% of listening' });
    if (sessionMax > 1000 * 60 * 60 * 8) badges.push({ name: 'Marathon Runner', icon: '🏃', description: '8+ hour listening session' });
    if (timeOfDayCount.morning > Math.max(timeOfDayCount.afternoon, timeOfDayCount.evening, timeOfDayCount.night)) badges.push({ name: 'Early Bird', icon: '🌅', description: 'Most active 6-9 AM' });
    const completionRate = parseFloat(completePercent);
    if (completionRate >= 90) badges.push({ name: 'Completionist', icon: '✅', description: '90%+ track completion rate' });
    if (topArtistPercent < 5) badges.push({ name: 'Variety King/Queen', icon: '🎭', description: 'Less than 5% from any single artist' });
    if (maxStreak >= 30) badges.push({ name: 'Dedicated', icon: '🔥', description: '30+ day listening streak' });
    if (uniqueTracks.size >= 500) badges.push({ name: 'Track Collector', icon: '💿', description: '500+ unique tracks' });
    if (weekendTracks > weekdayTracks * 1.5) badges.push({ name: 'Weekend Warrior', icon: '🎉', description: 'Weekend listening dominance' });

    // 2. Musical DNA Breakdown
    const totalListeningEvents = totalTrackPlays;
    const explorerScore = (uniqueArtists.size / totalListeningEvents) * 100;
    const bingeScore = binges.length > 0 ? (binges.reduce((sum, b) => sum + b.count, 0) / totalListeningEvents) * 100 : 0;
    const loyalScore = Math.min(topArtistPercent * 2, 100);
    const casualScore = Math.max(0, 100 - loyalScore - bingeScore);
    
    const musicalDNA = [
      { type: 'Loyal', percent: Math.round(loyalScore), description: 'Stick to favorites', color: '#FF6B6B' },
      { type: 'Explorer', percent: Math.round(Math.min(explorerScore * 5, 30)), description: 'Always discovering', color: '#4ECDC4' },
      { type: 'Binge Listener', percent: Math.round(Math.min(bingeScore * 10, 30)), description: 'Deep dives on repeat', color: '#45B7D1' },
      { type: 'Casual', percent: Math.round(Math.max(casualScore, 10)), description: 'Spread across everything', color: '#FFA07A' }
    ].sort((a, b) => b.percent - a.percent);

    // 3. Rare Gems (tracks played 1-3 times with high completion)
    const rareGems = Object.entries(trackPlayCount)
      .filter(([_, count]) => count >= 1 && count <= 3)
      .map(([track, count]) => ({ name: track, plays: count, ms: trackCount[track] }))
      .sort((a, b) => b.ms - a.ms)
      .slice(0, 10);

    // 4. Fun Predictions
    const daysInDataset = allDays.length || 1;
    const minutesPerDay = (totalMs / 1000 / 60) / daysInDataset;
    const daysToMillion = Math.ceil((1000000 - (totalMs / 1000 / 60)) / minutesPerDay);
    const soundSpeed = 343; // m/s
    const funFacts = [
      `At this rate, you'll hit 1M minutes by ${new Date(Date.now() + daysToMillion * 24 * 60 * 60 * 1000).toLocaleDateString()}`,
      `Enough listening time to fly around Earth ${((totalMs / 1000 / 60 / 60) / 24).toFixed(1)} times`,
      `Your music traveled ${((totalMs / 1000) * soundSpeed / 1000).toFixed(0)}km through the air`,
      `You could have listened to Beethoven's 9th Symphony ${Math.floor((totalMs / 1000 / 60) / 70)} times`
    ];

    // 5. Daily Mood Ring (color based on listening intensity)
    const dailyMoodRing = Object.entries(dailyHours).map(([day, hours]) => {
      let mood = '😴'; // low
      let color = '#6B7280';
      if (hours > 5) { mood = '🎉'; color = '#EF4444'; }
      else if (hours > 3) { mood = '🎵'; color = '#F59E0B'; }
      else if (hours > 1) { mood = '🎧'; color = '#10B981'; }
      return { day, hours, mood, color };
    });

    // 6. Listening Pairs Detective (artists frequently in same sessions)
    const sessionPairs: Record<string, Record<string, number>> = {};
    let currentSessionArtists = new Set<string>();
    let prevSessionTs: Date | null = null;
    
    sortedData.forEach((entry: any) => {
      const artist = entry.master_metadata_album_artist_name;
      const ts = entry.ts ? new Date(entry.ts) : null;
      
      if (ts && artist && artist !== 'Unknown') {
        if (!prevSessionTs || (ts.getTime() - prevSessionTs.getTime() > sessionThreshold)) {
          // New session, process previous
          const artists = Array.from(currentSessionArtists);
          for (let i = 0; i < artists.length; i++) {
            for (let j = i + 1; j < artists.length; j++) {
              const pair = [artists[i], artists[j]].sort().join(' & ');
              if (!sessionPairs[pair]) sessionPairs[pair] = {};
              sessionPairs[pair].count = (sessionPairs[pair].count || 0) + 1;
            }
          }
          currentSessionArtists = new Set();
        }
        currentSessionArtists.add(artist);
        prevSessionTs = ts;
      }
    });
    
    const listeningPairs = Object.entries(sessionPairs)
      .map(([pair, data]: [string, any]) => ({ pair, count: data.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // 7. Listening Efficiency Score
    const totalDays = allDays.length;
    const avgMinutesPerDay = totalDays > 0 ? (totalMs / 1000 / 60) / totalDays : 0;
    const minutesInDay = 24 * 60;
    const listeningEfficiency = ((avgMinutesPerDay / minutesInDay) * 100).toFixed(1);

    // 8. Party Mode Detector (days with unusual variety)
    const partyDays = Object.keys(dayCount).map(day => {
      const dayData = sortedData.filter((e: any) => e.ts && e.ts.startsWith(day));
      const dayArtists = new Set(dayData.map((e: any) => e.master_metadata_album_artist_name).filter((a: string) => a !== 'Unknown'));
      const artistsList = Array.from(dayArtists);
      return { day, artists: dayArtists.size, tracks: dayCount[day], artistNames: artistsList };
    }).filter(d => d.artists >= 20).sort((a, b) => b.artists - a.artists).slice(0, 10);

    // 9. Hall of Fame Records
    const hallOfFame = [
      { title: 'Most Consecutive Plays', value: mostPlayedTrack ? mostPlayedTrack[0] : 'N/A', stat: mostPlayedTrack ? `${mostPlayedTrack[1]} plays` : '', gradient: 'from-yellow-500 to-orange-500' },
      { title: 'Longest Track', value: longestTrack.ms > 0 ? longestTrack.name : 'N/A', stat: longestTrack.ms > 0 ? `${(longestTrack.ms / 1000 / 60).toFixed(2)} min` : '', gradient: 'from-purple-500 to-pink-500' },
      { title: 'Listening Streak', value: `${maxStreak} days`, stat: 'Consecutive days', gradient: 'from-green-500 to-teal-500' },
      { title: 'Biggest Binge', value: binges[0] ? binges[0].artist : 'N/A', stat: binges[0] ? `${binges[0].count} plays in one day` : '', gradient: 'from-red-500 to-pink-500' },
      { title: 'Marathon Session', value: sessionMax ? `${(sessionMax / 1000 / 60 / 60).toFixed(2)} hours` : 'N/A', stat: 'Longest session', gradient: 'from-blue-500 to-cyan-500' },
      { title: 'Top Discovery Month', value: monthlyDiscovery.length > 0 ? monthlyDiscovery.sort((a, b) => b.newArtists - a.newArtists)[0].month : 'N/A', stat: monthlyDiscovery.length > 0 ? `${monthlyDiscovery.sort((a, b) => b.newArtists - a.newArtists)[0].newArtists} new artists` : '', gradient: 'from-indigo-500 to-purple-500' }
    ];

    // 10. NEW INSIGHTS

    // Artist Discovery Timeline - when did you discover your top artists
    const artistDiscoveryTimeline = allArtistsList.slice(0, 10).map(artist => {
      const firstPlay = sortedData.find((e: any) => e.master_metadata_album_artist_name === artist.name);
      return {
        artist: artist.name,
        firstHeard: firstPlay ? firstPlay.ts : null,
        totalMs: artist.ms
      };
    }).filter(a => a.firstHeard).sort((a, b) => new Date(a.firstHeard!).getTime() - new Date(b.firstHeard!).getTime());

    // ENHANCED INSIGHTS - NEW

    // 1. Listening Velocity Analysis - speed of discovering new music
    const velocityAnalysis = monthlyDiscovery.map((month, idx) => {
      if (idx === 0) return { month: month.month, velocity: 0, trend: 'baseline' };
      const prev = monthlyDiscovery[idx - 1];
      const artistVelocity = month.newArtists - prev.newArtists;
      const trackVelocity = month.newTracks - prev.newTracks;
      const trend = artistVelocity > 0 ? 'accelerating' : artistVelocity < 0 ? 'slowing' : 'stable';
      return { 
        month: month.month, 
        velocity: artistVelocity + trackVelocity, 
        trend,
        artistVelocity,
        trackVelocity
      };
    });

    const avgVelocity = velocityAnalysis.reduce((sum, v) => sum + v.velocity, 0) / velocityAnalysis.length;
    const discoveryPhase = avgVelocity > 10 ? 'Explorer Mode' : avgVelocity > 0 ? 'Steady Discovery' : 'Comfort Zone';

    // 2. Artist Loyalty Score - how loyal are you to individual artists
    const artistLoyaltyScores = allArtistsList.slice(0, 20).map(artist => {
      const artistPlays = sortedData.filter((e: any) => e.master_metadata_album_artist_name === artist.name);
      const firstPlay = new Date(artistPlays[0]?.ts || Date.now());
      const lastPlay = new Date(artistPlays[artistPlays.length - 1]?.ts || Date.now());
      const daySpan = Math.max(1, (lastPlay.getTime() - firstPlay.getTime()) / (1000 * 60 * 60 * 24));
      const consistency = artistPlays.length / daySpan; // plays per day
      
      return {
        artist: artist.name,
        totalPlays: artistPlays.length,
        daySpan: Math.round(daySpan),
        consistency: consistency.toFixed(2),
        loyaltyScore: Math.min(100, consistency * 10)
      };
    });

    const topLoyalArtist = artistLoyaltyScores.sort((a, b) => b.loyaltyScore - a.loyaltyScore)[0];

    // 3. Listening Attention Span - how long do you stick with artists/tracks
    const attentionSpanData = allTracksList.slice(0, 50).map(track => {
      const plays = trackPlayCount[track.name] || 0;
      const firstIdx = sortedData.findIndex((e: any) => e.master_metadata_track_name === track.name);
      const lastIdx = sortedData.length - 1 - [...sortedData].reverse().findIndex((e: any) => e.master_metadata_track_name === track.name);
      const span = lastIdx - firstIdx;
      return { track: track.name, plays, span };
    });

    const avgAttentionSpan = attentionSpanData.reduce((sum, t) => sum + t.span, 0) / attentionSpanData.length;
    const attentionType = avgAttentionSpan > 1000 ? 'Long-term Lover' : avgAttentionSpan > 500 ? 'Moderate Returner' : 'Quick Mover';

    // 4. Prime Listening Window - your most productive listening time
    const primeWindows = hourlyPattern
      .map((h, idx) => ({
        hour: h.hour,
        count: h.count,
        nextHour: hourlyPattern[(idx + 1) % 24].count,
        prevHour: hourlyPattern[(idx - 1 + 24) % 24].count
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    const primeWindow = primeWindows[0] ? {
      startHour: primeWindows[0].hour,
      endHour: (primeWindows[0].hour + 2) % 24,
      count: primeWindows[0].count,
      percentage: ((primeWindows[0].count / totalTrackPlays) * 100).toFixed(1)
    } : null;

    // 5. Social Listening Score - variety of contexts you listen in
    const contextVariety = {
      platforms: allPlatforms.length,
      countries: allCountries.length,
      times: Object.values(timeOfDayCount).filter(c => c > 10).length,
      days: dayOfWeekPattern.filter(d => d.count > 10).length
    };

    const socialScore = Math.min(100, 
      (contextVariety.platforms * 15) + 
      (contextVariety.countries * 10) + 
      (contextVariety.times * 15) + 
      (contextVariety.days * 10)
    );

    const listeningStyle = socialScore > 70 ? 'Omnipresent Listener' : socialScore > 40 ? 'Flexible Listener' : 'Routine Listener';

    // 6. Genre Diversity Estimation (based on artist variety)
    const topArtistShare = topArtistPercent;
    const top5Share = allArtistsList.slice(0, 5).reduce((sum, a) => sum + (a.ms / totalMs) * 100, 0);
    const top10Share = allArtistsList.slice(0, 10).reduce((sum, a) => sum + (a.ms / totalMs) * 100, 0);

    const diversityScore = 100 - (top10Share * 0.7 + top5Share * 0.2 + topArtistShare * 0.1);
    const diversityLevel = diversityScore > 70 ? 'Highly Diverse' : diversityScore > 40 ? 'Moderately Diverse' : 'Focused Taste';

    // 7. Energy Level Analysis - based on listening patterns
    const morningEnergy = timeOfDayCount.morning / Math.max(1, totalTrackPlays) * 100;
    const lateNightEnergy = timeOfDayCount.night / Math.max(1, totalTrackPlays) * 100;
    const weekendEnergy = weekendTracks / Math.max(1, totalTrackPlays) * 100;

    const energyProfile = {
      morning: morningEnergy.toFixed(1),
      lateNight: lateNightEnergy.toFixed(1),
      weekend: weekendEnergy.toFixed(1),
      type: morningEnergy > 25 ? 'Morning Person' : lateNightEnergy > 25 ? 'Night Person' : 'Balanced'
    };

    // 8. Listening Evolution - how your taste changed over time
    const firstMonthArtists = new Set(
      sortedData.slice(0, Math.min(1000, sortedData.length))
        .map((e: any) => e.master_metadata_album_artist_name)
        .filter((a: string) => a !== 'Unknown')
    );
    
    const lastMonthArtists = new Set(
      sortedData.slice(-Math.min(1000, sortedData.length))
        .map((e: any) => e.master_metadata_album_artist_name)
        .filter((a: string) => a !== 'Unknown')
    );

    const artistOverlap = [...firstMonthArtists].filter(a => lastMonthArtists.has(a)).length;
    const evolutionScore = 100 - ((artistOverlap / Math.min(firstMonthArtists.size, lastMonthArtists.size)) * 100);
    const evolutionType = evolutionScore > 60 ? 'Rapid Evolution' : evolutionScore > 30 ? 'Steady Evolution' : 'Consistent Taste';

    // 10. Peak Performance Days - your best listening days
    const peakDays = Object.entries(dailyHours)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([day, hours]) => ({
        day,
        hours: hours.toFixed(1),
        tracks: dayCount[day] || 0,
        date: new Date(day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      }));

    // 11. Skip Behavior Intelligence
    const skipByArtist = allArtistsList.slice(0, 20).map(artist => {
      const artistTracks = sortedData.filter((e: any) => e.master_metadata_album_artist_name === artist.name);
      const skipped = artistTracks.filter((e: any) => e.skipped).length;
      const skipRate = (skipped / artistTracks.length) * 100;
      return { artist: artist.name, skipRate: skipRate.toFixed(1), skipped, total: artistTracks.length };
    }).filter(a => a.total > 10).sort((a, b) => parseFloat(b.skipRate) - parseFloat(a.skipRate));

    const mostSkippedArtist = skipByArtist[0];
    const leastSkippedArtist = skipByArtist[skipByArtist.length - 1];

    // 12. Listening Milestones
    const milestones = [];
    if (totalMs / 1000 / 60 / 60 >= 1000) milestones.push({ icon: '🏆', title: '1000 Hour Club', description: 'Reached 1000+ hours of listening' });
    if (uniqueArtists.size >= 500) milestones.push({ icon: '🎭', title: 'Artist Connoisseur', description: '500+ unique artists discovered' });
    if (uniqueTracks.size >= 2000) milestones.push({ icon: '💎', title: 'Track Master', description: '2000+ unique tracks played' });
    if (maxStreak >= 100) milestones.push({ icon: '🔥', title: 'Century Streak', description: '100+ consecutive listening days' });
    if (sessionMax >= 1000 * 60 * 60 * 12) milestones.push({ icon: '⏰', title: 'Half Day Marathon', description: '12+ hour listening session' });
    if (totalTrackPlays >= 10000) milestones.push({ icon: '🎵', title: 'Ten Thousand Club', description: '10,000+ tracks played' });

    // Session characteristics - analyze session types
    const sessions: Array<{start: Date, end: Date, duration: number, tracks: number}> = [];
    let currentSession: {start: Date, tracks: number, duration: number} | null = null;
    let lastTs: Date | null = null;
    
    sortedData.forEach((entry: any) => {
      const ts = entry.ts ? new Date(entry.ts) : null;
      const ms = entry.ms_played || 0;
      
      if (ts) {
        if (!currentSession || !lastTs || (ts.getTime() - lastTs.getTime() > sessionThreshold)) {
          if (currentSession && lastTs) {
            sessions.push({
              start: currentSession.start,
              end: lastTs,
              duration: currentSession.duration,
              tracks: currentSession.tracks
            });
          }
          currentSession = { start: ts, tracks: 1, duration: ms };
        } else {
          currentSession.tracks++;
          currentSession.duration += ms;
        }
        lastTs = ts;
      }
    });
    
    if (currentSession && lastTs) {
      const session: {start: Date, tracks: number, duration: number} = currentSession;
      sessions.push({
        start: session.start,
        end: lastTs,
        duration: session.duration,
        tracks: session.tracks
      });
    }

    const shortSessions = sessions.filter(s => s.duration < 1000 * 60 * 30).length;
    const mediumSessions = sessions.filter(s => s.duration >= 1000 * 60 * 30 && s.duration < 1000 * 60 * 120).length;
    const longSessions = sessions.filter(s => s.duration >= 1000 * 60 * 120).length;
    const avgSessionDuration = sessions.length > 0 ? sessions.reduce((sum, s) => sum + s.duration, 0) / sessions.length : 0;
    const avgTracksPerSession = sessions.length > 0 ? sessions.reduce((sum, s) => sum + s.tracks, 0) / sessions.length : 0;

    // Time-of-day artist preferences
    const artistTimePreferences: Record<string, Record<string, number>> = {};
    sortedData.forEach((entry: any) => {
      const artist = entry.master_metadata_album_artist_name;
      const ts = entry.ts ? new Date(entry.ts) : null;
      if (ts && artist && artist !== 'Unknown') {
        const hour = ts.getHours();
        let timeOfDay = 'night';
        if (hour >= 5 && hour < 12) timeOfDay = 'morning';
        else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
        else if (hour >= 17 && hour < 22) timeOfDay = 'evening';
        
        if (!artistTimePreferences[artist]) artistTimePreferences[artist] = { morning: 0, afternoon: 0, evening: 0, night: 0 };
        artistTimePreferences[artist][timeOfDay]++;
      }
    });

    const morningArtist = Object.entries(artistTimePreferences)
      .map(([artist, times]: [string, any]) => ({ artist, count: times.morning }))
      .sort((a, b) => b.count - a.count)[0];
    const afternoonArtist = Object.entries(artistTimePreferences)
      .map(([artist, times]: [string, any]) => ({ artist, count: times.afternoon }))
      .sort((a, b) => b.count - a.count)[0];
    const eveningArtist = Object.entries(artistTimePreferences)
      .map(([artist, times]: [string, any]) => ({ artist, count: times.evening }))
      .sort((a, b) => b.count - a.count)[0];
    const nightArtist = Object.entries(artistTimePreferences)
      .map(([artist, times]: [string, any]) => ({ artist, count: times.night }))
      .sort((a, b) => b.count - a.count)[0];

    // Listening consistency - variance in daily listening
    const hoursPerDay = Object.values(dailyHours);
    const avgHours = hoursPerDay.reduce((a, b) => a + b, 0) / hoursPerDay.length;
    const variance = hoursPerDay.reduce((sum, hours) => sum + Math.pow(hours - avgHours, 2), 0) / hoursPerDay.length;
    const stdDev = Math.sqrt(variance);
    const consistencyScore = Math.max(0, 100 - (stdDev / avgHours * 100));

    // Repeat behavior - tracks played multiple times
    const heavyRotation = Object.entries(trackPlayCount)
      .filter(([_, count]) => count >= 10)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([track, count]) => ({ track, count }));

    // Listening velocity trends - month over month
    const monthlyVelocity = monthlyDiscovery.map((month, idx) => {
      if (idx === 0) return { ...month, change: 0 };
      const prev = monthlyDiscovery[idx - 1];
      const change = ((month.hours - prev.hours) / prev.hours * 100).toFixed(0);
      return { ...month, change: parseFloat(change) };
    });

    // Skip patterns by time of day
    const skipByTimeOfDay = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    const totalByTimeOfDay = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    sortedData.forEach((entry: any) => {
      const skipped = entry.skipped || false;
      const ts = entry.ts ? new Date(entry.ts) : null;
      if (ts) {
        const hour = ts.getHours();
        let timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night' = 'night';
        if (hour >= 5 && hour < 12) timeOfDay = 'morning';
        else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
        else if (hour >= 17 && hour < 22) timeOfDay = 'evening';
        
        totalByTimeOfDay[timeOfDay]++;
        if (skipped) skipByTimeOfDay[timeOfDay]++;
      }
    });

    const skipRatesByTime = {
      morning: totalByTimeOfDay.morning > 0 ? (skipByTimeOfDay.morning / totalByTimeOfDay.morning * 100).toFixed(1) : '0',
      afternoon: totalByTimeOfDay.afternoon > 0 ? (skipByTimeOfDay.afternoon / totalByTimeOfDay.afternoon * 100).toFixed(1) : '0',
      evening: totalByTimeOfDay.evening > 0 ? (skipByTimeOfDay.evening / totalByTimeOfDay.evening * 100).toFixed(1) : '0',
      night: totalByTimeOfDay.night > 0 ? (skipByTimeOfDay.night / totalByTimeOfDay.night * 100).toFixed(1) : '0'
    };

    // Most diverse listening day
    const mostDiverseDay = Object.keys(dayCount).map(day => {
      const dayData = sortedData.filter((e: any) => e.ts && e.ts.startsWith(day));
      const dayArtists = new Set(dayData.map((e: any) => e.master_metadata_album_artist_name).filter((a: string) => a !== 'Unknown'));
      const dayTracks = new Set(dayData.map((e: any) => e.master_metadata_track_name).filter((t: string) => t !== 'Unknown'));
      return { day, artists: dayArtists.size, tracks: dayTracks.size };
    }).sort((a, b) => (b.artists + b.tracks) - (a.artists + a.tracks))[0];

    // Seasonal patterns
    const seasonalData: Record<string, {tracks: number, hours: number, artists: Set<string>}> = {
      'Winter': { tracks: 0, hours: 0, artists: new Set() },
      'Spring': { tracks: 0, hours: 0, artists: new Set() },
      'Summer': { tracks: 0, hours: 0, artists: new Set() },
      'Fall': { tracks: 0, hours: 0, artists: new Set() }
    };

    sortedData.forEach((entry: any) => {
      const ts = entry.ts ? new Date(entry.ts) : null;
      const ms = entry.ms_played || 0;
      const artist = entry.master_metadata_album_artist_name;
      if (ts) {
        const month = ts.getMonth(); // 0-11
        let season = 'Winter';
        if (month >= 2 && month <= 4) season = 'Spring';
        else if (month >= 5 && month <= 7) season = 'Summer';
        else if (month >= 8 && month <= 10) season = 'Fall';
        
        seasonalData[season].tracks++;
        seasonalData[season].hours += ms / 1000 / 60 / 60;
        if (artist && artist !== 'Unknown') seasonalData[season].artists.add(artist);
      }
    });

    const seasonalPatterns = Object.entries(seasonalData).map(([season, data]) => ({
      season,
      tracks: data.tracks,
      hours: data.hours.toFixed(1),
      artists: data.artists.size
    })).sort((a, b) => b.tracks - a.tracks);

    // Comeback tracks - tracks you stopped listening to then returned to
    const trackTimeline: Record<string, number[]> = {};
    sortedData.forEach((entry: any, idx: number) => {
      const track = entry.master_metadata_track_name;
      if (track && track !== 'Unknown') {
        if (!trackTimeline[track]) trackTimeline[track] = [];
        trackTimeline[track].push(idx);
      }
    });

    const comebackTracks = Object.entries(trackTimeline)
      .filter(([_, plays]) => plays.length >= 5)
      .map(([track, plays]) => {
        const gaps = [];
        for (let i = 1; i < plays.length; i++) {
          gaps.push(plays[i] - plays[i-1]);
        }
        const maxGap = Math.max(...gaps);
        const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
        return { track, maxGap, avgGap, totalPlays: plays.length };
      })
      .filter(t => t.maxGap > 100) // significant gap
      .sort((a, b) => b.maxGap - a.maxGap)
      .slice(0, 10);

    // Rediscovery Rate - tracks/artists you came back to (must be after comebackTracks)
    const rediscoveries = comebackTracks.length;
    const rediscoveryRate = (rediscoveries / uniqueTracks.size) * 100;
    const rediscoveryType = rediscoveryRate > 10 ? 'Nostalgic' : rediscoveryRate > 5 ? 'Occasional Returner' : 'Forward-Focused';

    // ========== ADVANCED ANALYTICS ==========

    // 1. Listening Momentum Analysis - track how listening intensity changes over time
    const weeklyMomentum: Array<{week: number, hours: number, tracks: number, artists: number, date: string}> = [];
    const weekLength = 7 * 24 * 60 * 60 * 1000; // 7 days in ms
    
    if (sortedData.length > 0) {
      const startTime = new Date(sortedData[0].ts).getTime();
      const endTime = new Date(sortedData[sortedData.length - 1].ts).getTime();
      const totalWeeks = Math.ceil((endTime - startTime) / weekLength);
      
      for (let week = 0; week < totalWeeks; week++) {
        const weekStart = startTime + (week * weekLength);
        const weekEnd = weekStart + weekLength;
        
        const weekData = sortedData.filter((e: any) => {
          const ts = new Date(e.ts).getTime();
          return ts >= weekStart && ts < weekEnd;
        });
        
        const weekHours = weekData.reduce((sum: number, e: any) => sum + (e.ms_played || 0), 0) / 1000 / 60 / 60;
        const weekArtists = new Set(weekData.map((e: any) => e.master_metadata_album_artist_name).filter((a: string) => a !== 'Unknown')).size;
        
        weeklyMomentum.push({
          week,
          hours: weekHours,
          tracks: weekData.length,
          artists: weekArtists,
          date: new Date(weekStart).toLocaleDateString()
        });
      }
    }
    
    const momentumTrend = weeklyMomentum.map((w, idx) => {
      if (idx === 0) return { ...w, change: '0', momentum: 'baseline' as const };
      const prev = weeklyMomentum[idx - 1];
      const change = prev.hours > 0 ? ((w.hours - prev.hours) / prev.hours * 100) : 0;
      const momentum = change > 20 ? 'accelerating' as const : change < -20 ? 'decelerating' as const : 'stable' as const;
      return { ...w, change: change.toFixed(1), momentum };
    });
    
    const avgMomentumChange = momentumTrend.length > 1 
      ? momentumTrend.slice(1).reduce((sum, w) => sum + (typeof w.change === 'string' ? parseFloat(w.change) : w.change), 0) / (momentumTrend.length - 1)
      : 0;
    
    const momentumType = avgMomentumChange > 5 ? 'Growing Obsession' : avgMomentumChange < -5 ? 'Fading Interest' : 'Steady State';
    
    // 2. Track Completion Prediction Score - likelihood of completing tracks
    const trackCompletionScores = allTracksList.slice(0, 50).map(track => {
      const trackPlays = sortedData.filter((e: any) => e.master_metadata_track_name === track.name);
      const completed = trackPlays.filter((e: any) => !e.skipped && e.reason_end !== 'fwdbtn').length;
      const completionRate = trackPlays.length > 0 ? (completed / trackPlays.length) * 100 : 0;
      const avgMsPlayed = trackPlays.reduce((sum: number, e: any) => sum + (e.ms_played || 0), 0) / trackPlays.length;
      
      return {
        track: track.name,
        completionRate: completionRate.toFixed(1),
        plays: trackPlays.length,
        avgMsPlayed: (avgMsPlayed / 1000).toFixed(0)
      };
    }).sort((a, b) => parseFloat(b.completionRate) - parseFloat(a.completionRate));
    
    const overallCompletionScore = trackCompletionScores.length > 0
      ? trackCompletionScores.reduce((sum, t) => sum + parseFloat(t.completionRate), 0) / trackCompletionScores.length
      : 0;
    
    const completionPersonality = overallCompletionScore > 80 ? 'Patient Listener' : 
                                 overallCompletionScore > 60 ? 'Selective Listener' : 'Impatient Skipper';

    // 3. Artist Network Analysis - find artists that are often listened to together
    const artistCoOccurrence: Record<string, Record<string, number>> = {};
    
    sessions.forEach(session => {
      const sessionStart = session.start.getTime();
      const sessionEnd = session.end.getTime();
      
      const sessionArtists = sortedData
        .filter((e: any) => {
          const ts = new Date(e.ts).getTime();
          return ts >= sessionStart && ts <= sessionEnd;
        })
        .map((e: any) => e.master_metadata_album_artist_name)
        .filter((a: string) => a !== 'Unknown');
      
      const uniqueSessionArtists = Array.from(new Set(sessionArtists));
      
      for (let i = 0; i < uniqueSessionArtists.length; i++) {
        for (let j = i + 1; j < uniqueSessionArtists.length; j++) {
          const artist1 = uniqueSessionArtists[i];
          const artist2 = uniqueSessionArtists[j];
          
          if (!artistCoOccurrence[artist1]) artistCoOccurrence[artist1] = {};
          if (!artistCoOccurrence[artist2]) artistCoOccurrence[artist2] = {};
          
          artistCoOccurrence[artist1][artist2] = (artistCoOccurrence[artist1][artist2] || 0) + 1;
          artistCoOccurrence[artist2][artist1] = (artistCoOccurrence[artist2][artist1] || 0) + 1;
        }
      }
    });
    
    const artistNetworks = Object.entries(artistCoOccurrence)
      .map(([artist, partners]) => {
        const topPartners = Object.entries(partners)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([partner, count]) => ({ partner, count }));
        
        const totalConnections = Object.values(partners).reduce((sum: number, count: any) => sum + count, 0);
        
        return {
          artist,
          topPartners,
          networkSize: Object.keys(partners).length,
          totalConnections
        };
      })
      .filter(a => a.networkSize > 0)
      .sort((a, b) => b.networkSize - a.networkSize)
      .slice(0, 10);
    
    const mostConnectedArtist = artistNetworks[0];
    const avgNetworkSize = artistNetworks.length > 0
      ? artistNetworks.reduce((sum, a) => sum + a.networkSize, 0) / artistNetworks.length
      : 0;
    
    const networkType = avgNetworkSize > 20 ? 'Highly Connected' : avgNetworkSize > 10 ? 'Moderately Connected' : 'Isolated Listening';

    // 4. Listening Habits Clustering - identify distinct listening modes
    const clusterData = sessions.map(session => {
      const sessionStart = session.start.getTime();
      const sessionEnd = session.end.getTime();
      
      const sessionData = sortedData.filter((e: any) => {
        const ts = new Date(e.ts).getTime();
        return ts >= sessionStart && ts <= sessionEnd;
      });
      
      const hour = session.start.getHours();
      const isWeekend = [0, 6].includes(session.start.getDay());
      const artistCount = new Set(sessionData.map((e: any) => e.master_metadata_album_artist_name)).size;
      const skipRate = sessionData.filter((e: any) => e.skipped).length / sessionData.length;
      
      return {
        duration: session.duration / 1000 / 60, // minutes
        tracks: session.tracks,
        artistVariety: artistCount,
        skipRate: skipRate * 100,
        timeOfDay: hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 22 ? 'evening' : 'night',
        isWeekend
      };
    });
    
    // Simple clustering based on duration and variety
    const focusedSessions = clusterData.filter(s => s.duration > 60 && s.artistVariety < 5).length;
    const exploreSessions = clusterData.filter(s => s.duration > 30 && s.artistVariety > 10).length;
    const quickSessions = clusterData.filter(s => s.duration < 30).length;
    const bingesSessions = clusterData.filter(s => s.duration > 120).length;
    
    const dominantMode = Math.max(focusedSessions, exploreSessions, quickSessions, bingesSessions);
    const listeningMode = dominantMode === focusedSessions ? 'Focused Listener' :
                         dominantMode === exploreSessions ? 'Explorer Mode' :
                         dominantMode === quickSessions ? 'Quick Hits' : 'Binge Listener';
    
    const modeDistribution = {
      focused: focusedSessions,
      explore: exploreSessions,
      quick: quickSessions,
      binge: bingesSessions
    };

    // 5. Deep Listening Score - how deeply you engage with music
    const deepListeningMetrics = {
      avgCompletionRate: overallCompletionScore,
      heavyRotationPercent: (heavyRotation.length / uniqueTracks.size) * 100,
      sessionDepth: avgTracksPerSession,
      artistLoyalty: topLoyalArtist ? topLoyalArtist.loyaltyScore : 0,
      attentionSpan: Math.min(100, avgAttentionSpan / 10)
    };
    
    const deepListeningScore = (
      deepListeningMetrics.avgCompletionRate * 0.3 +
      deepListeningMetrics.heavyRotationPercent * 0.2 +
      Math.min(100, deepListeningMetrics.sessionDepth * 2) * 0.2 +
      deepListeningMetrics.artistLoyalty * 0.2 +
      deepListeningMetrics.attentionSpan * 0.1
    );
    
    const deepListeningLevel = deepListeningScore > 75 ? 'Deep Diver' :
                              deepListeningScore > 50 ? 'Engaged Listener' :
                              deepListeningScore > 25 ? 'Casual Listener' : 'Surface Skimmer';

    // 6. Recommendation Affinity - how open you are to new music
    const newArtistsPerMonth = monthlyDiscovery.map(m => m.newArtists);
    const avgNewArtists = newArtistsPerMonth.reduce((a, b) => a + b, 0) / newArtistsPerMonth.length;
    const newArtistTrend = newArtistsPerMonth.length > 6 
      ? ((newArtistsPerMonth.slice(-3).reduce((a, b) => a + b, 0) / 3) - 
         (newArtistsPerMonth.slice(0, 3).reduce((a, b) => a + b, 0) / 3))
      : 0;
    
    const discoveryOpenness = (avgNewArtists / uniqueArtists.size) * 100;
    const recommendationAffinity = {
      openness: discoveryOpenness.toFixed(1),
      trend: newArtistTrend > 0 ? 'Growing' : newArtistTrend < 0 ? 'Declining' : 'Stable',
      avgNewPerMonth: avgNewArtists.toFixed(0),
      personality: discoveryOpenness > 15 ? 'Discovery Enthusiast' :
                   discoveryOpenness > 8 ? 'Balanced Explorer' :
                   discoveryOpenness > 3 ? 'Selective Discoverer' : 'Comfort Zone Dweller'
    };

    // 7. Listening Fragmentation Score - how scattered your listening is
    const fragmentationMetrics = {
      singlePlayRate: (singlePlayTracks.size / uniqueTracks.size) * 100,
      artistConcentration: top10Share,
      sessionConsistency: parseFloat(consistencyScore.toFixed(0)),
      modeVariety: Object.values(modeDistribution).filter(v => v > 0).length
    };
    
    const fragmentationScore = (
      (100 - fragmentationMetrics.singlePlayRate) * 0.3 +
      (100 - fragmentationMetrics.artistConcentration) * 0.3 +
      fragmentationMetrics.sessionConsistency * 0.3 +
      (100 - fragmentationMetrics.modeVariety * 15) * 0.1
    );
    
    const fragmentationType = fragmentationScore > 70 ? 'Focused Flow' :
                             fragmentationScore > 40 ? 'Balanced Mix' :
                             fragmentationScore > 20 ? 'Scattered Listening' : 'Musical Butterfly';

    // 8. Trend Forecasting - predict future patterns
    const recentTrends = monthlyDiscovery.slice(-3);
    const historicalAvg = monthlyDiscovery.slice(0, -3);
    
    if (recentTrends.length === 3 && historicalAvg.length > 0) {
      const recentAvgHours = recentTrends.reduce((sum, m) => sum + m.hours, 0) / 3;
      const historicalAvgHours = historicalAvg.reduce((sum, m) => sum + m.hours, 0) / historicalAvg.length;
      const hoursTrend = ((recentAvgHours - historicalAvgHours) / historicalAvgHours * 100).toFixed(0);
      
      const recentAvgArtists = recentTrends.reduce((sum, m) => sum + m.newArtists, 0) / 3;
      const historicalAvgArtists = historicalAvg.reduce((sum, m) => sum + m.newArtists, 0) / historicalAvg.length;
      const artistsTrend = ((recentAvgArtists - historicalAvgArtists) / historicalAvgArtists * 100).toFixed(0);
      
      var trendForecast = {
        listeningVolume: parseFloat(hoursTrend) > 10 ? '📈 Increasing' : 
                        parseFloat(hoursTrend) < -10 ? '📉 Decreasing' : '➡️ Stable',
        discoveryRate: parseFloat(artistsTrend) > 10 ? '🚀 Accelerating' :
                      parseFloat(artistsTrend) < -10 ? '🐌 Slowing' : '➡️ Steady',
        prediction: parseFloat(hoursTrend) > 10 && parseFloat(artistsTrend) > 10 ? 'Musical Renaissance Incoming!' :
                   parseFloat(hoursTrend) < -10 && parseFloat(artistsTrend) < -10 ? 'Taking a Break?' :
                   parseFloat(hoursTrend) > 10 ? 'Deepening Your Favorites' :
                   parseFloat(artistsTrend) > 10 ? 'Exploration Phase' : 'Steady Listening Pattern'
      };
    } else {
      var trendForecast = {
        listeningVolume: '➡️ Stable',
        discoveryRate: '➡️ Steady',
        prediction: 'Need more data for prediction'
      };
    }

    // 9. Listening Personality Profile - comprehensive personality assessment
    const personalityTraits = {
      explorer: (discoveryOpenness / 15 * 100), // max 15% = 100 points
      loyalist: (artistLoyaltyScores[0]?.loyaltyScore || 0),
      completionist: overallCompletionScore,
      binger: Math.min(100, (bingesSessions / sessions.length) * 200),
      eclectic: diversityScore,
      consistent: parseFloat(consistencyScore.toFixed(0)),
      social: socialScore,
      deepDiver: deepListeningScore
    };
    
    const topTraits = Object.entries(personalityTraits)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([trait, score]) => ({
        trait: trait.charAt(0).toUpperCase() + trait.slice(1),
        score: Math.min(100, score).toFixed(0)
      }));
    
    const personalityArchetype = 
      personalityTraits.explorer > 70 ? '🗺️ The Musical Explorer' :
      personalityTraits.loyalist > 70 ? '❤️ The Devoted Fan' :
      personalityTraits.binger > 70 ? '🎧 The Marathon Listener' :
      personalityTraits.eclectic > 70 ? '🎨 The Genre Blender' :
      personalityTraits.deepDiver > 70 ? '🤿 The Deep Listener' :
      personalityTraits.consistent > 70 ? '📅 The Routine Listener' :
      '🎵 The Balanced Music Lover';

    return {
      dateFrom: formattedFromDate,
      dateTo: formattedToDate,
      totalHours: (totalMs / 1000 / 60 / 60).toFixed(2),
      topTracks,
      topArtists,
      topAlbums,
      topPlatform: topPlatforms ? { name: topPlatforms[0], count: topPlatforms[1] } : null,
      topCountry: topCountries ? { name: topCountries[0], count: topCountries[1] } : null,
      totalTracks: data.length,
      uniqueArtists: uniqueArtists.size,
      avgTrackLength: totalTrackPlays ? (totalTrackLength / totalTrackPlays / 1000 / 60).toFixed(2) : '0',
      mostSkipped: mostSkipped ? { name: mostSkipped[0], count: mostSkipped[1] } : null,
      mostPlayedDay: mostPlayedDay ? { day: mostPlayedDay[0], count: mostPlayedDay[1] } : null,
      mostPlayedTrack: mostPlayedTrack ? { name: mostPlayedTrack[0], count: mostPlayedTrack[1] } : null,
      mostCommonHour: mostCommonHour ? { hour: mostCommonHour[0], count: mostCommonHour[1] } : null,
      mostCommonReasonEnd: mostCommonReasonEnd ? { reason: mostCommonReasonEnd[0], count: mostCommonReasonEnd[1] } : null,
      firstTrack,
      lastTrack,
      longestTrack: longestTrack.ms > 0 ? longestTrack : null,
      shortestTrack: shortestTrack.ms < Infinity ? shortestTrack : null,
      sessionMax: sessionMax ? { duration: sessionMax, start: sessionStartMax, end: sessionEndMax } : null,
      uniqueTracks: uniqueTracks.size,
      singlePlayTracks: singlePlayTracks.size,
      mostRepeatedArtistDay: mostRepeatedArtistDay.count > 0 ? mostRepeatedArtistDay : null,
      listeningDays: allDays.length,
      maxStreak,
      skipPercent,
      completePercent,
      // New metrics
      weekendTracks,
      weekdayTracks,
      timePersona,
      timeOfDayCount,
      monthlyDiscovery,
      binges: binges.slice(0, 10),
      avgTracksPerDay,
      avgHoursPerDay,
      allPlatforms,
      allCountries,
      hourlyPattern,
      dayOfWeekPattern,
      dailyHours: Object.entries(dailyHours).map(([day, hours]) => ({ day, hours })).sort((a, b) => a.day.localeCompare(b.day)),
      allDays,
      // Full lists for search
      allArtistsList,
      allTracksList,
      allAlbumsList,
      // New features
      badges,
      musicalDNA,
      rareGems,
      funFacts,
      dailyMoodRing,
      listeningPairs,
      listeningEfficiency,
      partyDays,
      hallOfFame,
      // New insights
      artistDiscoveryTimeline,
      sessionCharacteristics: {
        total: sessions.length,
        short: shortSessions,
        medium: mediumSessions,
        long: longSessions,
        avgDuration: avgSessionDuration,
        avgTracks: avgTracksPerSession
      },
      artistTimePreferences: {
        morning: morningArtist,
        afternoon: afternoonArtist,
        evening: eveningArtist,
        night: nightArtist
      },
      listeningConsistency: consistencyScore.toFixed(0),
      heavyRotation,
      monthlyVelocity,
      skipRatesByTime,
      mostDiverseDay,
      seasonalPatterns,
      comebackTracks,
      // Enhanced insights
      discoveryPhase,
      velocityAnalysis,
      topLoyalArtist,
      attentionType,
      avgAttentionSpan,
      primeWindow,
      socialScore,
      listeningStyle,
      diversityScore,
      diversityLevel,
      energyProfile,
      rediscoveryRate,
      rediscoveryType,
      evolutionScore,
      evolutionType,
      peakDays,
      skipByArtist: skipByArtist.slice(0, 5),
      mostSkippedArtist,
      leastSkippedArtist,
      milestones,
      artistLoyaltyScores: artistLoyaltyScores.slice(0, 10),
      // Advanced Analytics
      momentumType,
      momentumTrend: momentumTrend.slice(-12), // last 12 weeks
      avgMomentumChange: avgMomentumChange.toFixed(1),
      trackCompletionScores: trackCompletionScores.slice(0, 10),
      overallCompletionScore: overallCompletionScore.toFixed(1),
      completionPersonality,
      artistNetworks,
      mostConnectedArtist,
      avgNetworkSize: avgNetworkSize.toFixed(1),
      networkType,
      listeningMode,
      modeDistribution,
      deepListeningScore: deepListeningScore.toFixed(1),
      deepListeningLevel,
      deepListeningMetrics,
      recommendationAffinity,
      fragmentationScore: fragmentationScore.toFixed(1),
      fragmentationType,
      fragmentationMetrics,
      trendForecast,
      personalityTraits,
      topTraits,
      personalityArchetype,
      isSpotifyMode: false, // File upload mode has full timeline data
    };
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setError(null);
    setIsProcessing(true);
    setProcessingProgress(0);

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Please upload a valid JSON file.');
      setIsProcessing(false);
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 50MB.');
      setIsProcessing(false);
      return;
    }

    const reader = new FileReader();
    
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 50); // File reading is 50% of progress
        setProcessingProgress(progress);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
      setIsProcessing(false);
      setProcessingProgress(0);
    };

    reader.onload = (e) => {
      try {
        setProcessingProgress(60);
        const text = e.target?.result as string;
        
        if (!text || text.trim().length === 0) {
          throw new Error('File is empty');
        }

        setProcessingProgress(70);
        const data = JSON.parse(text);
        
        // Validate data structure
        if (!Array.isArray(data)) {
          throw new Error('Invalid format: Expected an array of streaming history');
        }

        if (data.length === 0) {
          throw new Error('No listening data found in file');
        }

        // Check if it looks like Spotify data
        const firstEntry = data[0];
        if (!firstEntry || typeof firstEntry !== 'object') {
          throw new Error('Invalid data structure');
        }

        // Validate required fields
        const requiredFields = ['ts', 'ms_played'];
        const hasRequiredFields = requiredFields.some(field => field in firstEntry);
        
        if (!hasRequiredFields) {
          throw new Error('This doesn\'t appear to be a Spotify Extended Streaming History file. Make sure you\'re uploading the correct JSON file.');
        }

        setProcessingProgress(80);
        const summary = generateWrappedSummary(data);
        
        if (!summary) {
          throw new Error('Failed to process data. Please check the file format.');
        }

        setProcessingProgress(100);
        setSummary(summary);
        setError(null);
        setIsProcessing(false);
        
        // Reset progress after a short delay
        setTimeout(() => setProcessingProgress(0), 500);
      } catch (err) {
        console.error('Processing error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Invalid JSON file. Please ensure you\'re uploading a valid Spotify Extended Streaming History file.';
        setError(errorMessage);
        setSummary(null);
        setIsProcessing(false);
        setProcessingProgress(0);
      }
    };
    
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Create a synthetic event for handleFileUpload
      const fileList = new DataTransfer();
      fileList.items.add(file);
      const syntheticEvent = {
        target: { files: fileList.files },
        currentTarget: { files: fileList.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(syntheticEvent);
    }
  };

  const UploadView = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="max-w-4xl w-full relative z-10">
        <div className="text-center mb-10 sm:mb-12 animate-fade-in">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <Music className="w-12 h-12 text-green-400" />
            <h1 className="text-3xl sm:text-5xl font-bold text-white">Spotify History Analyzer</h1>
          </div>
          <p className="text-gray-300 text-base sm:text-lg">Visualize your musical journey with real-time data or file upload</p>
        </div>

        {/* File Upload Option - Primary */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-5 sm:p-8 border border-gray-700/50 shadow-2xl hover:border-purple-500/50 transition-all duration-300">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-3">Upload Your Data & Discover</h2>
              <p className="text-gray-300 text-base mb-2">Deep dive into your complete listening history</p>
              <p className="text-purple-300 text-sm font-medium">🎁 Unveil Wrapped mysteries • 🔍 Find hidden patterns • ⏰ Track hourly trends • 🎭 Uncover surprises</p>
            </div>

            <label 
            htmlFor="file-upload" 
            className={`block border-2 border-dashed rounded-2xl p-6 sm:p-12 text-center transition-all duration-300 cursor-pointer group ${
              isDragging 
                ? 'border-green-500 bg-green-500/10 scale-105' 
                : isProcessing 
                ? 'border-blue-500 bg-blue-500/5 cursor-wait' 
                : 'border-gray-600 hover:border-green-500 hover:bg-green-500/5'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="mb-4 flex justify-center">
              <div className={`p-4 rounded-full transition-all duration-300 ${
                isDragging ? 'bg-green-500/30' : isProcessing ? 'bg-blue-500/20' : 'bg-green-500/10 group-hover:bg-green-500/20'
              }`}>
                {isProcessing ? (
                  <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload className={`w-12 h-12 text-green-400 transition-transform ${
                    isDragging ? 'scale-125' : 'group-hover:scale-110'
                  }`} />
                )}
              </div>
            </div>
            {isProcessing ? (
              <>
                <p className="text-white font-medium mb-2">Processing your data...</p>
                <div className="max-w-md mx-auto">
                  <div className="bg-gray-700 rounded-full h-2 overflow-hidden mb-2">
                    <div 
                      className="bg-gradient-to-r from-blue-400 to-green-400 h-full transition-all duration-300 ease-out"
                      style={{ width: `${processingProgress}%` }}
                    />
                  </div>
                  <p className="text-blue-300 text-sm">{processingProgress}% complete</p>
                </div>
              </>
            ) : isDragging ? (
              <>
                <p className="text-white font-medium mb-2">Drop your file here</p>
                <p className="text-gray-400 text-sm">Release to upload</p>
              </>
            ) : (
              <>
                <p className="text-white font-medium mb-2">Click to upload or drag and drop</p>
                <p className="text-gray-500 text-sm">Supports JSON files from Spotify data export (max 50MB)</p>
              </>
            )}
            <input 
              id="file-upload" 
              type="file" 
              accept="application/json,.json" 
              onChange={handleFileUpload} 
              className="hidden" 
              disabled={isProcessing}
              aria-label="Upload Spotify data file"
            />
          </label>

          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="w-4 h-4 text-purple-400" />
              <span>Complete history</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <span>Detailed analytics</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Zap className="w-4 h-4 text-purple-400" />
              <span>100% private</span>
            </div>
          </div>

          {/* How to get your data */}
          <div className="mt-6 p-4 bg-purple-900/30 rounded-xl border border-purple-700/50">
            <p className="text-sm text-purple-200">
              <span className="text-purple-400 font-medium">📦 How to get your data:</span> Request your <span className="font-semibold">Extended Streaming History</span> from <a href="https://www.spotify.com/account/privacy/" target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-purple-200 underline">Spotify's privacy settings</a>. Processing takes 1-30 days. You'll receive a download link via email.
            </p>
          </div>
        </div>
      </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-900/50 border border-red-700/50 rounded-xl animate-shake" role="alert">
            <div className="flex items-start gap-3">
              <div className="text-red-400 text-xl">⚠️</div>
              <div className="flex-1">
                <p className="text-red-300 font-medium mb-1">Error</p>
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Info Cards */}
        <div className="mt-6 space-y-3">
          <div className="p-4 bg-blue-900/30 rounded-xl border border-blue-700/50">
            <p className="text-sm text-blue-200">
              <span className="text-blue-400 font-medium">📊 Why use this?</span> Found something suspicious in your official Spotify Wrapped? This tool lets you dig deep into your raw listening data to verify stats, discover patterns, and analyze your music history in detail.
            </p>
          </div>
          <div className="p-4 bg-purple-900/30 rounded-xl border border-purple-700/50">
            <p className="text-sm text-purple-200">
              <span className="text-purple-400 font-medium">⚠️ Disclaimer:</span> This is an unofficial, fan-made project and is not affiliated with or endorsed by Spotify in any way. Built by a music enthusiast for fellow data nerds. Spotify is a trademark of Spotify AB.
            </p>
          </div>
          <div className="p-4 bg-gray-900/50 rounded-xl border border-gray-700/50">
            <p className="text-sm text-gray-400">
              <span className="text-green-400 font-medium">Need your data?</span> Request your Spotify Extended Streaming History from your <a href="https://www.spotify.com/account/privacy/" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 underline">account privacy settings</a>. Processing takes 1-30 days.
            </p>
          </div>
        </div>

        {/* Testimonial */}
        <div className="mt-8 bg-gradient-to-br from-green-900/20 to-emerald-900/20 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30">
          <div className="flex items-start gap-4">
            <div className="text-4xl">💡</div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-400 mb-2">Real Story: Mystery Solved!</h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                "My 2025 top artist was someone I didn't even recognize!. Turns out, during a trip, a single song played <span className="text-green-400 font-bold">238 times in one day...</span> on repeat. This tool's daily breakdown revealed exactly when and how it happened. My honor as a metalhead has been saved! 🤘 Mystery solved!"
              </p>
              <p className="text-gray-500 text-xs italic">— Use case: Understanding unexpected listening patterns</p>
            </div>
          </div>
        </div>

        {/* Spotify Login Option - Secondary */}
        <div className="mt-8 max-w-xl mx-auto">
          <div className="bg-gray-800/30 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/30">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-semibold text-white">Or Connect Your Spotify Account</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">Quick access with limited history (recent data only)</p>
            </div>

            <button
              onClick={handleSpotifyLogin}
              className="w-full bg-gray-700/50 hover:bg-green-600/80 border border-gray-600 hover:border-green-500 text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <LogIn className="w-4 h-4" />
              <span>Login with Spotify</span>
            </button>

            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <PlayCircle className="w-3 h-3 text-green-400" />
                <span>Audio previews</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3 text-green-400" />
                <span>Follow artists</span>
              </div>
              <div className="flex items-center gap-1">
                <Music className="w-3 h-3 text-green-400" />
                <span>Create playlists</span>
              </div>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-green-400" />
                <span>Genre insights</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ResultsView = () => {
    if (!summary) return null;

    const artistColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];

    // Optimized search with early exit and limited results
    const filteredArtists = useMemo(() => {
      if (!debouncedSearch) return summary.topArtists;
      const lowerQuery = debouncedSearch.toLowerCase();
      const results = [];
      const maxResults = 100; // Reduced to 100 for faster rendering
      
      for (const item of summary.allArtistsList) {
        if (results.length >= maxResults) break; // Early exit
        if (item.name.toLowerCase().includes(lowerQuery)) {
          results.push(item);
        }
      }
      return results;
    }, [debouncedSearch, summary.topArtists, summary.allArtistsList]);

    const filteredTracks = useMemo(() => {
      if (!debouncedSearch) return summary.topTracks;
      const lowerQuery = debouncedSearch.toLowerCase();
      const results = [];
      const maxResults = 100; // Reduced to 100 for faster rendering
      
      for (const item of summary.allTracksList) {
        if (results.length >= maxResults) break; // Early exit
        if (item.name.toLowerCase().includes(lowerQuery)) {
          results.push(item);
        }
      }
      return results;
    }, [debouncedSearch, summary.topTracks, summary.allTracksList]);

    const filteredAlbums = useMemo(() => {
      if (!debouncedSearch) return summary.topAlbums;
      const lowerQuery = debouncedSearch.toLowerCase();
      const results = [];
      const maxResults = 100; // Reduced to 100 for faster rendering
      
      for (const item of summary.allAlbumsList) {
        if (results.length >= maxResults) break; // Early exit
        if (item.name.toLowerCase().includes(lowerQuery)) {
          results.push(item);
        }
      }
      return results;
    }, [debouncedSearch, summary.topAlbums, summary.allAlbumsList]);

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 sm:p-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-4">
                <Music className="w-10 h-10 text-white" />
                <div>
                  <h1 className="text-2xl sm:text-4xl font-bold text-white">
                    Your Music Wrapped
                    {summary?.dateFrom && summary?.dateTo && (
                      <span className="text-xl text-green-100 font-normal block">
                        {summary.dateFrom} to {summary.dateTo}
                      </span>
                    )}
                  </h1>
                  <p className="text-green-100 mt-2">
                    {spotifyUser 
                      ? `${spotifyUser.display_name}'s listening stats` 
                      : 'A year of musical memories'}
                  </p>
                  {spotifyUser && (
                    <p className="text-green-100 mt-2">
                      ⚠️ Limited to recent data - Timeline, Patterns, and Insights tabs not available. Logout and request your full history if you wanna deep dive
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap justify-end">
                {spotifyUser && (
                  <div className="flex items-center gap-3 px-4 py-2 bg-white/10 rounded-lg border border-white/20">
                    {spotifyUser.images?.[0]?.url ? (
                      <img src={spotifyUser.images[0].url} alt={spotifyUser.display_name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <UserCircle className="w-8 h-8 text-white" />
                    )}
                    <span className="text-white font-medium hidden sm:inline">{spotifyUser.display_name}</span>
                  </div>
                )}
                {spotifyUser && spotifyData && (
                  <button
                    onClick={createTopTracksPlaylist}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center gap-2 border border-white/20"
                    title="Create playlist from top tracks"
                  >
                    <Music className="w-4 h-4" />
                    <span className="hidden sm:inline">Create Playlist</span>
                  </button>
                )}
                {spotifyUser ? (
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-white rounded-lg transition-all duration-300 flex items-center gap-2 border border-red-500/30"
                    aria-label="Logout from Spotify"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Logout</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setSummary(null);
                      setError(null);
                      setSearchQuery('');
                      setActiveTab('overview');
                      stopAudio();
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center gap-2 border border-white/20"
                    aria-label="Upload new file"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">New Upload</span>
                  </button>
                )}
                {(activeTab === 'artists' || activeTab === 'tracks') && (
                <div className="w-full sm:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={activeTab === 'artists' ? 'Search artists...' : 'Search tracks...'}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="pl-10 pr-10 py-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/30 w-full sm:w-64"
                      autoComplete="off"
                      spellCheck="false"
                    />
                    {isSearching ? (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                      </div>
                    ) : searchQuery && (
                      <button
                        onClick={() => { setSearchQuery(''); setDebouncedSearch(''); searchInputRef.current?.focus(); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {searchQuery.length > 0 && searchQuery.length < 2 && (
                    <div className="text-xs text-gray-400 mt-1">Type at least 2 characters to search</div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-xl border-b border-gray-700/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <nav className="flex gap-6 overflow-x-auto" role="tablist" aria-label="Music analysis tabs">
              {(['overview', 'artists', 'tracks', 'timeline', 'patterns', 'insights', 'advanced'] as const)
                .filter(tab => {
                  // Hide timeline, patterns, insights, and advanced tabs for Spotify mode
                  if (summary.isSpotifyMode && ['timeline', 'patterns', 'insights', 'advanced'].includes(tab)) {
                    return false;
                  }
                  return true;
                })
                .map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  role="tab"
                  aria-selected={activeTab === tab}
                  aria-controls={`${tab}-panel`}
                  className={`py-4 px-2 font-medium capitalize transition-all duration-300 border-b-2 whitespace-nowrap ${
                    activeTab === tab 
                      ? 'text-green-400 border-green-400' 
                      : 'text-gray-400 border-transparent hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-4 sm:p-8">
          {activeTab === 'overview' && (
            <div role="tabpanel" id="overview-panel" aria-labelledby="overview-tab" className="space-y-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Clock, label: 'Total Hours', value: summary.totalHours, gradient: 'from-green-500 to-emerald-600' },
                  { icon: Headphones, label: 'Tracks Played', value: summary.totalTracks.toLocaleString(), gradient: 'from-purple-500 to-pink-600' },
                  { icon: Music, label: 'Unique Artists', value: summary.uniqueArtists.toLocaleString(), gradient: 'from-blue-500 to-cyan-600' },
                  { icon: Zap, label: 'Longest Streak', value: `${summary.maxStreak} days`, gradient: 'from-orange-500 to-red-600' }
                ].map((stat, idx) => (
                  <div key={idx} className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 hover:scale-105 transition-transform duration-300">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${stat.gradient} mb-4`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Listening Habits
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Tracks Completed</span>
                      <span className="text-green-400 font-semibold">{summary.completePercent}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{width: `${summary.completePercent}%`}}></div>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <span className="text-gray-300">Tracks Skipped</span>
                      <span className="text-orange-400 font-semibold">{summary.skipPercent}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="bg-gradient-to-r from-orange-400 to-red-500 h-2 rounded-full" style={{width: `${summary.skipPercent}%`}}></div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    Activity Stats
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Listening Days</span>
                      <span className="text-white font-semibold">{summary.listeningDays}/365</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Avg Track Length</span>
                      <span className="text-white font-semibold">{summary.avgTrackLength} min</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Tracks Played Once</span>
                      <span className="text-white font-semibold">{summary.singlePlayTracks.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  Interesting Facts
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {summary.firstTrack && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-green-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🔰</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">First Track</div>
                          <div className="text-white font-semibold">{summary.firstTrack.track}</div>
                          <div className="text-sm text-gray-400">by {summary.firstTrack.artist}</div>
                          <div className="text-xs text-gray-500 mt-1">{new Date(summary.firstTrack.ts).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.lastTrack && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-green-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🏁</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Last Track</div>
                          <div className="text-white font-semibold">{summary.lastTrack.track}</div>
                          <div className="text-sm text-gray-400">by {summary.lastTrack.artist}</div>
                          <div className="text-xs text-gray-500 mt-1">{new Date(summary.lastTrack.ts).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostPlayedDay && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-purple-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🏆</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Most Active Day</div>
                          <div className="text-white font-semibold">{summary.mostPlayedDay.day}</div>
                          <div className="text-sm text-purple-400">{summary.mostPlayedDay.count} tracks played</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostPlayedTrack && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-green-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🔂</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Most Played Track</div>
                          <div className="text-white font-semibold">{summary.mostPlayedTrack.name}</div>
                          <div className="text-sm text-green-400">{summary.mostPlayedTrack.count} plays</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostSkipped && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-orange-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">⏭️</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Most Skipped Track</div>
                          <div className="text-white font-semibold">{summary.mostSkipped.name}</div>
                          <div className="text-sm text-orange-400">{summary.mostSkipped.count} skips</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostCommonHour && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🕒</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Peak Listening Hour</div>
                          <div className="text-white font-semibold">{summary.mostCommonHour.hour}:00</div>
                          <div className="text-sm text-blue-400">{summary.mostCommonHour.count} tracks</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.sessionMax && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-pink-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🕰️</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Longest Session</div>
                          <div className="text-white font-semibold">{Math.round(summary.sessionMax.duration / 1000 / 60)} minutes</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {summary.sessionMax.start ? new Date(summary.sessionMax.start).toLocaleString() : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.longestTrack && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-cyan-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📏</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Longest Track</div>
                          <div className="text-white font-semibold text-sm">{summary.longestTrack.name}</div>
                          <div className="text-sm text-cyan-400">{(summary.longestTrack.ms / 1000 / 60).toFixed(1)} minutes</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.shortestTrack && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-yellow-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📐</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Shortest Track</div>
                          <div className="text-white font-semibold text-sm">{summary.shortestTrack.name}</div>
                          <div className="text-sm text-yellow-400">{(summary.shortestTrack.ms / 1000 / 60).toFixed(1)} minutes</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.topPlatform && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-indigo-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📱</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Favorite Platform</div>
                          <div className="text-white font-semibold">{summary.topPlatform.name}</div>
                          <div className="text-sm text-indigo-400">{summary.topPlatform.count} plays</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.topCountry && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-emerald-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🌍</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Top Listening Country</div>
                          <div className="text-white font-semibold">{summary.topCountry.name}</div>
                          <div className="text-sm text-emerald-400">{summary.topCountry.count} plays</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostCommonReasonEnd && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-rose-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">❓</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Most Common End Reason</div>
                          <div className="text-white font-semibold">{summary.mostCommonReasonEnd.reason}</div>
                          <div className="text-sm text-rose-400">{summary.mostCommonReasonEnd.count} times</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary.mostRepeatedArtistDay && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-violet-500/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🔁</div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-400 mb-1">Most Repeated Artist in a Day</div>
                          <div className="text-white font-semibold">{summary.mostRepeatedArtistDay.artist}</div>
                          <div className="text-sm text-violet-400">{summary.mostRepeatedArtistDay.count} plays on {summary.mostRepeatedArtistDay.day}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'artists' && (
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Award className="w-8 h-8 text-yellow-400" />
                Top Artists {debouncedSearch && `(${filteredArtists.length} results)`}
              </h2>
              
              {/* Top 10 Artists Bar Chart */}
              {!debouncedSearch && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 mb-8">
                  <h3 className="text-xl font-semibold text-white mb-6">Top 10 Artists by Listening Time</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={filteredArtists.slice(0, 10).map((a: any) => ({ 
                        name: a.name.length > 20 ? a.name.substring(0, 20) + '...' : a.name,
                        fullName: a.name,
                        minutes: (a.ms / 1000 / 60).toFixed(1),
                        hours: (a.ms / 1000 / 60 / 60).toFixed(1)
                      }))}
                      layout="vertical"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} label={{ value: 'Minutes', position: 'insideBottom', offset: -5, fill: '#9ca3af' }} />
                      <YAxis type="category" dataKey="name" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} width={90} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(_value: any, _name: any, props: any) => [`${props.payload.hours} hours`, props.payload.fullName]}
                      />
                      <Bar dataKey="minutes" fill="#22c55e" radius={[0, 8, 8, 0]}>
                        {filteredArtists.slice(0, 10).map((_: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={artistColors[index % artistColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {filteredArtists.length === 0 && debouncedSearch ? (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-12 border border-gray-700/50 text-center">
                  <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No artists found matching "{debouncedSearch}"</p>
                </div>
              ) : (
              <div className="space-y-4">
                {filteredArtists.map((artist: any, idx: number) => (
                  <a 
                    key={idx} 
                    href={`https://open.spotify.com/search/${encodeURIComponent(artist.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 hover:border-green-500/50 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`text-2xl font-bold w-12 h-12 flex items-center justify-center rounded-xl ${
                        idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                        idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                        idx === 2 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-gray-700/50 text-gray-400'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-green-400 transition-colors">{artist.name}</h3>
                        <div className="flex items-center gap-4">
                          <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-1000 group-hover:scale-105`}
                              style={{
                                width: `${(artist.ms / (filteredArtists[0]?.ms || 1)) * 100}%`,
                                background: `linear-gradient(to right, ${artistColors[idx % artistColors.length]}, ${artistColors[idx % artistColors.length]}dd)`
                              }}
                            ></div>
                          </div>
                          <span className="text-gray-300 font-medium w-24 text-right">{(artist.ms / 1000 / 60).toFixed(1)} min</span>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              )}

              <div className="mt-12">
                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <Music className="w-8 h-8 text-purple-400" />
                  Top Albums {debouncedSearch && `(${filteredAlbums.length} results)`}
                </h2>
                {filteredAlbums.length === 0 && debouncedSearch ? (
                  <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 border border-gray-700/50 text-center">
                    <p className="text-gray-400">No albums found matching "{debouncedSearch}"</p>
                  </div>
                ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {filteredAlbums.map((album: any, idx: number) => (
                    <div key={idx} className="bg-gray-800/50 backdrop-blur-xl rounded-xl p-5 border border-gray-700/50 hover:border-purple-500/30 transition-all duration-300">
                      <div className="flex items-center gap-4">
                        <div className="text-2xl font-bold text-gray-400 w-10 h-10 flex items-center justify-center rounded-lg bg-gray-700/50">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{album.name}</h3>
                          <div className="text-sm text-purple-400 mt-1">{(album.ms / 1000 / 60).toFixed(0)} minutes</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'tracks' && (
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Play className="w-8 h-8 text-green-400" />
                Top Tracks {debouncedSearch && `(${filteredTracks.length} results)`}
              </h2>

              {/* Top 15 Tracks Bar Chart */}
              {!debouncedSearch && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 mb-8">
                  <h3 className="text-xl font-semibold text-white mb-6">Top 15 Tracks by Listening Time</h3>
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart 
                      data={filteredTracks.slice(0, 15).map((t: any) => ({ 
                        name: t.name.length > 25 ? t.name.substring(0, 25) + '...' : t.name,
                        fullName: t.name,
                        minutes: (t.ms / 1000 / 60).toFixed(1),
                        hours: (t.ms / 1000 / 60 / 60).toFixed(1)
                      }))}
                      layout="vertical"
                      margin={{ left: 150 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} label={{ value: 'Minutes', position: 'insideBottom', offset: -5, fill: '#9ca3af' }} />
                      <YAxis type="category" dataKey="name" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} width={140} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(_value: any, _name: any, props: any) => [`${props.payload.hours} hours`, props.payload.fullName]}
                      />
                      <Bar dataKey="minutes" fill="#10b981" radius={[0, 8, 8, 0]}>
                        {filteredTracks.slice(0, 15).map((_: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={index < 3 ? '#22c55e' : index < 10 ? '#10b981' : '#059669'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {filteredTracks.length === 0 && debouncedSearch ? (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-12 border border-gray-700/50 text-center">
                  <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No tracks found matching "{debouncedSearch}"</p>
                </div>
              ) : (
              <div className="space-y-3">
                {filteredTracks.map((track: any, idx: number) => {
                  const hasPreview = (track.preview_url || track.previewUrl) && (track.preview_url || track.previewUrl) !== null;
                  const previewUrl = track.preview_url || track.previewUrl;
                  const trackId = track.id || track.name;
                  const isPlaying = currentlyPlayingId === trackId;
                  
                  return (
                    <div 
                      key={idx} 
                      className="bg-gray-800/50 backdrop-blur-xl rounded-xl p-5 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-lg font-bold text-gray-400 w-8">{idx + 1}</div>
                        <div className="flex-1">
                          <h3 className="text-white font-medium">{track.name}</h3>
                          <p className="text-sm text-gray-400 mt-1">{(track.ms / 1000 / 60).toFixed(2)} minutes played</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasPreview ? (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                playPreview(previewUrl, trackId);
                              }}
                              className={`p-2 rounded-lg transition-all duration-300 flex items-center justify-center ${
                                isPlaying
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-700 text-gray-300 hover:bg-green-500 hover:text-white'
                              }`}
                              title={isPlaying ? 'Stop preview' : 'Play preview'}
                            >
                              {isPlaying ? (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <rect x="5" y="4" width="3" height="12" />
                                  <rect x="12" y="4" width="3" height="12" />
                                </svg>
                              ) : (
                                <Play className="w-5 h-5" />
                              )}
                            </button>
                          ) : (
                            <button
                              disabled
                              className="p-2 rounded-lg bg-gray-700/50 text-gray-500 cursor-not-allowed"
                              title="No preview available"
                            >
                              <PlayCircle className="w-5 h-5" />
                            </button>
                          )}
                          <a 
                            href={`https://open.spotify.com/search/${encodeURIComponent(track.name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-gray-700 hover:bg-green-600 text-gray-300 hover:text-white transition-all"
                            title="Open on Spotify"
                          >
                            <ExternalLink className="w-5 h-5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-green-400" />
                Listening Timeline
              </h2>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <div className="text-sm text-gray-400 mb-2">Avg Tracks/Day</div>
                  <div className="text-3xl font-bold text-white mb-2">{summary.avgTracksPerDay}</div>
                </div>
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <div className="text-sm text-gray-400 mb-2">Avg Hours/Day</div>
                  <div className="text-3xl font-bold text-white mb-2">{summary.avgHoursPerDay}</div>
                </div>
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <div className="text-sm text-gray-400 mb-2">Active Days</div>
                  <div className="text-3xl font-bold text-white mb-2">{summary.listeningDays}</div>
                </div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-xl font-semibold text-white mb-6">Daily Listening Hours (Last 60 Days)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={summary.dailyHours.slice(-60).map((d: any) => ({ ...d, date: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }))}>
                    <defs>
                      <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9ca3af" 
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      interval="preserveStartEnd"
                      minTickGap={50}
                    />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area type="monotone" dataKey="hours" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {summary.monthlyDiscovery && summary.monthlyDiscovery.length > 0 && (
                <>
                  <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                    <h3 className="text-xl font-semibold text-white mb-6">Monthly Discovery Rate</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={summary.monthlyDiscovery.slice().sort((a: any, b: any) => {
                        const [yearA, monthA] = a.month.split('-').map(Number);
                        const [yearB, monthB] = b.month.split('-').map(Number);
                        return yearA !== yearB ? yearA - yearB : monthA - monthB;
                      }).map((m: any) => ({ 
                        ...m, 
                        monthLabel: new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) 
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="monthLabel" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="newArtists" stroke="#22c55e" strokeWidth={2} name="New Artists" />
                        <Line type="monotone" dataKey="newTracks" stroke="#3b82f6" strokeWidth={2} name="New Tracks" />
                        <Line type="monotone" dataKey="hours" stroke="#a855f7" strokeWidth={2} name="Hours" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                    <h3 className="text-xl font-semibold text-white mb-6">Monthly Listening Volume</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={summary.monthlyDiscovery.slice().sort((a: any, b: any) => {
                        const [yearA, monthA] = a.month.split('-').map(Number);
                        const [yearB, monthB] = b.month.split('-').map(Number);
                        return yearA !== yearB ? yearA - yearB : monthA - monthB;
                      }).map((m: any) => ({ 
                        ...m, 
                        monthLabel: new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short' }) 
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="monthLabel" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                        <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="tracks" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Tracks Played" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {summary.binges && summary.binges.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-green-400" />
                    🔥 Binge Listening Sessions
                  </h3>
                  <div className="space-y-3">
                    {summary.binges.map((binge: any, idx: number) => (
                      <div key={idx} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30 hover:border-green-500/30 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-white font-medium truncate">{binge.artist}</div>
                            <div className="text-sm text-gray-400">{new Date(binge.day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                          </div>
                          <div className="text-white font-bold text-lg">{binge.count}x</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'patterns' && (
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-purple-400" />
                Listening Patterns
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    {summary.timePersona === 'morning' && <Sunrise className="w-6 h-6 text-yellow-400" />}
                    {summary.timePersona === 'afternoon' && <Sun className="w-6 h-6 text-orange-400" />}
                    {summary.timePersona === 'evening' && <Sunset className="w-6 h-6 text-pink-400" />}
                    {summary.timePersona === 'night' && <Moon className="w-6 h-6 text-blue-400" />}
                    Time of Day Persona
                  </h3>
                  
                  {/* Pie Chart for Time of Day */}
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(summary.timeOfDayCount).map(([time, count]) => ({ 
                          name: time.charAt(0).toUpperCase() + time.slice(1), 
                          value: count 
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { cx, cy, midAngle, outerRadius, name, percent } = props;
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 25;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          return (
                            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="14" fontWeight="500">
                              {`${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#fbbf24" />
                        <Cell fill="#fb923c" />
                        <Cell fill="#f472b6" />
                        <Cell fill="#60a5fa" />
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div className="space-y-3 mt-4">
                    {Object.entries(summary.timeOfDayCount).map(([time, count]: [string, any]) => (
                      <div key={time}>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-300 capitalize">{time}</span>
                          <span className="text-white font-medium">{count}</span>
                        </div>
                        <div className="bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 group-hover:scale-105`}
                            style={{
                              width: `${(count / Math.max(...(Object.values(summary.timeOfDayCount) as number[]))) * 100}%`,
                              backgroundColor: count > 0 ? `${artistColors[Math.floor(Math.random() * artistColors.length)]}dd` : 'transparent'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 bg-gray-900/50 rounded-lg border border-gray-700/30">
                    <div className="text-sm text-gray-400">You're a</div>
                    <div className="text-xl font-bold text-white capitalize">{summary.timePersona} Listener</div>
                  </div>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-green-400" />
                    Weekend vs Weekday
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
                      <div className="text-sm mb-2 opacity-90">Weekday</div>
                      <div className="text-3xl font-bold text-white mb-2">{summary.weekdayTracks}</div>
                      <div className="text-sm mt-2 opacity-75">tracks</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-6 text-white">
                      <div className="text-sm mb-2 opacity-90">Weekend</div>
                      <div className="text-3xl font-bold text-white mb-2">{summary.weekendTracks}</div>
                      <div className="text-sm mt-2 opacity-75">tracks</div>
                    </div>
                  </div>

                  {/* Pie Chart for Weekend vs Weekday */}
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Weekday', value: summary.weekdayTracks },
                          { name: 'Weekend', value: summary.weekendTracks }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { cx, cy, midAngle, outerRadius, name, percent } = props;
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 25;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          return (
                            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="14" fontWeight="500">
                              {`${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#3b82f6" />
                        <Cell fill="#a855f7" />
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  <div className="space-y-3 mt-6">
                    {summary.dayOfWeekPattern.map((day: any) => (
                      <div key={day.day}>
                        <div className="flex justify-between mb-1">
                          <span className="text-white capitalize">{day.day}</span>
                          <span className="text-gray-400 font-medium">{day.count}</span>
                        </div>
                        <div className="bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-full rounded-full ${
                              day.day === 'Saturday' || day.day === 'Sunday' 
                                ? 'bg-gradient-to-r from-purple-400 to-pink-400'
                                : 'bg-gradient-to-r from-blue-400 to-cyan-400'
                            }`}
                            style={{width: `${(day.count / Math.max(...summary.dayOfWeekPattern.map((d: any) => d.count))) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Radar Chart for Day of Week */}
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-white mb-4">Weekly Listening Pattern</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={summary.dayOfWeekPattern}>
                        <PolarGrid stroke="#374151" />
                        <PolarAngleAxis dataKey="day" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                        <PolarRadiusAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                        <Radar name="Tracks Played" dataKey="count" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-orange-400" />
                  24-Hour Listening Heatmap
                </h3>
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 mb-6">
                  {summary.hourlyPattern.map((hour: any) => {
                    const maxCount = Math.max(...summary.hourlyPattern.map((h: any) => h.count));
                    const intensity = hour.count / maxCount;
                    return (
                      <div key={hour.hour} className="flex flex-col items-center gap-2">
                        <div 
                          className="w-full aspect-square rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer relative group"
                          style={{
                            backgroundColor: intensity > 0 
                              ? `rgba(34, 197, 94, ${0.2 + intensity * 0.8})`
                              : 'rgba(55, 65, 81, 0.3)'
                          }}
                          title={`${hour.hour}:00 - ${hour.count} tracks`}
                        >
                          <div className="absolute inset-0 flex items-center justify-center text-xs text-white opacity-0 group-hover:opacity-100 font-medium">
                            {hour.count}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">{hour.hour}</div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Hourly Bar Chart */}
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={summary.hourlyPattern}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="hour" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#22c55e' }}
                    />
                    <Bar dataKey="count" fill="#22c55e" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {summary.allPlatforms && summary.allPlatforms.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Smartphone className="w-6 h-6 text-cyan-400" />
                    Platform Breakdown
                  </h3>
                  
                  {/* Platform Pie Chart */}
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={summary.allPlatforms.slice(0, 6)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { cx, cy, midAngle, outerRadius, name, percent } = props;
                          if ((percent || 0) <= 0.05) return null;
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 25;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          return (
                            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="14" fontWeight="500">
                              {`${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                            </text>
                          );
                        }}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {summary.allPlatforms.slice(0, 6).map((_: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'][index]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                        labelStyle={{ color: '#fff' }}
                        itemStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div className="space-y-3 mt-4">
                    {summary.allPlatforms.slice(0, 5).map((platform: any, idx: number) => (
                      <div key={idx}>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-300">{platform.name}</span>
                          <span className="text-white font-medium">{platform.count} plays</span>
                        </div>
                        <div className="bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-cyan-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${(platform.count / summary.allPlatforms[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {summary.allCountries && summary.allCountries.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-emerald-400" />
                    Listening Locations
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {summary.allCountries.slice(0, 10).map((country: any, idx: number) => (
                      <div key={idx} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30 hover:border-emerald-500/30 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-white font-medium">{country.name}</div>
                            <div className="text-sm text-gray-400">{country.count} plays</div>
                          </div>
                        </div>
                      </div>
                    ))}
                                   </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-yellow-400" />
                Your Music Personality
              </h2>
              
              {/* Achievements/Badges */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  Achievements Unlocked ({summary.badges.length})
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary.badges.map((badge: any, idx: number) => (
                    <div key={idx} className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 rounded-xl p-6 border border-yellow-500/20 hover:border-yellow-500/50 transition-all duration-300 group">
                      <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{badge.icon}</div>
                      <h4 className="text-white font-bold text-lg mb-2">{badge.name}</h4>
                      <p className="text-gray-400 text-sm">{badge.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Musical DNA */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                  Musical DNA Breakdown
                </h3>
                <div className="space-y-4">
                  {summary.musicalDNA.map((dna: any, idx: number) => (
                    <div key={idx}>
                      <div className="flex justify-between mb-2">
                        <div>
                          <span className="text-white font-medium">{dna.type}</span>
                          <span className="text-gray-400 text-sm ml-2">• {dna.description}</span>
                        </div>
                        <span className="text-gray-300 font-semibold">{dna.percent}%</span>
                      </div>
                      <div className="bg-gray-700 rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-1000"
                          style={{
                            width: `${dna.percent}%`,
                            backgroundColor: dna.color
                          }}
                        >
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hall of Fame */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Award className="w-6 h-6 text-amber-400" />
                  Hall of Fame
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {summary.hallOfFame.map((record: any, idx: number) => (
                    <div key={idx} className={`bg-gradient-to-br ${record.gradient} rounded-xl p-6 text-white`}>
                      <h4 className="text-sm font-medium opacity-90 mb-2">{record.title}</h4>
                      <p className="text-xl font-bold mb-1 line-clamp-1">{record.value}</p>
                      <p className="text-sm opacity-80">{record.stat}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rare Gems */}
              {summary.rareGems.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-pink-400" />
                    Rare Gems (Hidden Treasures)
                  </h3>
                  <p className="text-gray-400 mb-4">Songs you only played 1-3 times but listened through</p>
                  <div className="space-y-2">
                    {summary.rareGems.slice(0, 8).map((gem: any, idx: number) => (
                      <a 
                        key={idx} 
                        href={`https://open.spotify.com/search/${encodeURIComponent(gem.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-gray-900/50 rounded-lg p-4 border border-pink-500/20 hover:border-pink-500/50 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium flex-1 line-clamp-1 group-hover:text-pink-300 transition-colors">{gem.name}</span>
                          <span className="text-pink-400 text-sm ml-4">{gem.plays} play{gem.plays > 1 ? 's' : ''}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Listening Pairs Detective */}
              {summary.listeningPairs.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-400" />
                    Musical Combos
                  </h3>
                  <p className="text-gray-400 mb-4">Artists you frequently listen to in the same sessions</p>
                  <div className="space-y-3">
                    {summary.listeningPairs.map((pair: any, idx: number) => (
                      <div key={idx} className="bg-gray-900/50 rounded-lg p-4 border border-blue-500/20 hover:border-blue-500/40 transition-all">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-white font-medium truncate">{pair.pair}</div>
                            <div className="text-sm text-gray-400">{pair.count} sessions</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Party Mode Detector */}
              {summary.partyDays.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    🎪 Party Mode Detected
                  </h3>
                  <p className="text-gray-400 mb-4">Days with unusual variety - you were exploring!</p>
                  <div className="space-y-3">
                    {summary.partyDays.map((day: any, idx: number) => (
                      <div key={idx} className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg p-4 border border-purple-500/30">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-white font-medium">{new Date(day.day).toLocaleDateString()}</div>
                            <div className="text-sm text-gray-400">{day.tracks} tracks</div>
                          </div>
                          <div className="text-3xl font-bold text-purple-400">{day.artists} artists</div>
                        </div>
                        {day.artistNames && day.artistNames.length > 0 && (
                          <div className="text-xs text-gray-400 line-clamp-2">
                            {day.artistNames.slice(0, 10).join(', ')}{day.artistNames.length > 10 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fun Facts */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-400" />
                  Fun Predictions & Comparisons
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {summary.funFacts.map((fact: string, idx: number) => (
                    <div key={idx} className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 rounded-xl p-5 border border-yellow-500/20">
                      <p className="text-white text-sm leading-relaxed">{fact}</p>
                    </div>
                  ))}
                  <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-xl p-5 border border-green-500/20">
                    <p className="text-white text-sm leading-relaxed">
                      <span className="font-bold text-green-400">{summary.listeningEfficiency}%</span> of your waking hours are spent with music
                    </p>
                  </div>
                </div>
              </div>

              {/* Daily Mood Ring Calendar Preview */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-indigo-400" />
                  Daily Mood Ring (Recent Days)
                </h3>
                <p className="text-gray-400 mb-4">Each day gets a mood based on listening intensity</p>
                <div className="grid grid-cols-5 sm:grid-cols-7 gap-2 mb-6">
                  {summary.dailyMoodRing.slice(-35).map((day: any, idx: number) => (
                    <div 
                      key={idx}
                      className="aspect-square rounded-lg flex items-center justify-center text-2xl hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: day.color + '33', borderColor: day.color, borderWidth: '2px' }}
                      title={`${day.day}: ${day.hours.toFixed(1)}h - ${day.mood}`}
                    >
                      {day.mood}
                    </div>
                  ))}
                </div>
              </div>

              {/* Artist Discovery Timeline */}
              {summary.artistDiscoveryTimeline.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-cyan-400" />
                    Your Artist Discovery Journey
                  </h3>
                  <p className="text-gray-400 mb-4">When you discovered your top artists</p>
                  <div className="space-y-3">
                    {summary.artistDiscoveryTimeline.map((item: any, idx: number) => (
                      <div key={idx} className="bg-gray-900/50 rounded-lg p-4 border border-cyan-500/20 hover:border-cyan-500/50 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-white font-medium truncate">{item.artist}</div>
                            <div className="text-cyan-400 text-sm">{new Date(item.firstHeard).toLocaleDateString()}</div>
                          </div>
                          <div className="text-white font-bold text-lg">{(item.totalMs / 1000 / 60).toFixed(0)} min</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Session Characteristics */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-orange-400" />
                  Listening Session Analysis
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-xl p-5 border border-blue-500/20">
                    <div className="text-gray-400 text-sm mb-2">Short Sessions</div>
                    <div className="text-3xl font-bold text-white mb-2">{summary.sessionCharacteristics.short}</div>
                    <div className="text-xs text-gray-400">Less than 30 minutes</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-5 border border-purple-500/20">
                    <div className="text-gray-400 text-sm mb-2">Medium Sessions</div>
                    <div className="text-3xl font-bold text-white mb-2">{summary.sessionCharacteristics.medium}</div>
                    <div className="text-xs text-gray-400">30 minutes to 2 hours</div>
                  </div>
                  <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 rounded-xl p-5 border border-red-500/20">
                    <div className="text-gray-400 text-sm mb-2">Marathon Sessions</div>
                    <div className="text-3xl font-bold text-white mb-2">{summary.sessionCharacteristics.long}</div>
                    <div className="text-xs text-gray-400">More than 2 hours</div>
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Avg Session Duration</div>
                    <div className="text-xl font-bold text-white">{(summary.sessionCharacteristics.avgDuration / 1000 / 60).toFixed(0)} minutes</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Avg Tracks per Session</div>
                    <div className="text-xl font-bold text-white">{summary.sessionCharacteristics.avgTracks.toFixed(1)} tracks</div>
                  </div>
                </div>
              </div>

              {/* Time-of-Day Artist Preferences */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Sun className="w-6 h-6 text-yellow-400" />
                  Your Artists by Time of Day
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 rounded-xl p-5 border border-yellow-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sunrise className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-300 font-semibold">Morning (5AM-12PM)</span>
                    </div>
                    <div className="text-white font-bold text-lg mb-2">{summary.artistTimePreferences.morning?.artist || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">{summary.artistTimePreferences.morning?.count || 0} plays</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-xl p-5 border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sun className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-300 font-semibold">Afternoon (12PM-5PM)</span>
                    </div>
                    <div className="text-white font-bold text-lg mb-2">{summary.artistTimePreferences.afternoon?.artist || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">{summary.artistTimePreferences.afternoon?.count || 0} plays</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-5 border border-orange-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sunset className="w-5 h-5 text-orange-400" />
                      <span className="text-orange-300 font-semibold">Evening (5PM-10PM)</span>
                    </div>
                    <div className="text-white font-bold text-lg mb-2">{summary.artistTimePreferences.evening?.artist || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">{summary.artistTimePreferences.evening?.count || 0} plays</div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-5 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Moon className="w-5 h-5 text-indigo-400" />
                      <span className="text-indigo-300 font-semibold">Night (10PM-5AM)</span>
                    </div>
                    <div className="text-white font-bold text-lg mb-2">{summary.artistTimePreferences.night?.artist || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">{summary.artistTimePreferences.night?.count || 0} plays</div>
                  </div>
                </div>
              </div>

              {/* Heavy Rotation */}
              {summary.heavyRotation.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Music className="w-6 h-6 text-red-400" />
                    Heavy Rotation (10+ Plays)
                  </h3>
                  <p className="text-gray-400 mb-4">Tracks you couldn't stop playing</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {summary.heavyRotation.map((item: any, idx: number) => (
                      <div key={idx} className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-lg p-4 border border-red-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium flex-1 line-clamp-1">{item.track}</span>
                          <span className="text-red-400 font-bold ml-3">{item.count}×</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Listening Consistency */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  Listening Consistency Score
                </h3>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <div className="bg-gray-700 rounded-full h-8 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-green-400 to-emerald-500 h-8 rounded-full flex items-center justify-end pr-4 transition-all duration-1000"
                        style={{width: `${summary.listeningConsistency}%`}}
                      >
                        <span className="text-white font-bold text-sm">{summary.listeningConsistency}%</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-3">
                      {parseFloat(summary.listeningConsistency) >= 70 ? 'Very consistent! You have steady listening habits.' :
                       parseFloat(summary.listeningConsistency) >= 40 ? 'Moderately consistent. Some variation in daily listening.' :
                       'Highly variable. Your listening changes day to day.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Skip Rates by Time of Day */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-purple-400" />
                  When Are You Most Picky?
                </h3>
                <p className="text-gray-400 mb-4">Skip rates by time of day</p>
                <div className="space-y-3">
                  {Object.entries(summary.skipRatesByTime).map(([time, rate]: [string, any]) => (
                    <div key={time}>
                      <div className="flex justify-between mb-2">
                        <span className="text-white capitalize">{time}</span>
                        <span className="text-purple-300">{rate}% skipped</span>
                      </div>
                      <div className="bg-gray-700 rounded-full h-2">
                        <div className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full" style={{width: `${rate}%`}}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Seasonal Patterns */}
              {summary.seasonalPatterns.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-teal-400" />
                    Seasonal Listening Patterns
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {summary.seasonalPatterns.map((season: any, idx: number) => (
                      <div key={idx} className={`rounded-xl p-5 border ${
                        season.season === 'Winter' ? 'bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border-blue-500/20' :
                        season.season === 'Spring' ? 'bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-green-500/20' :
                        season.season === 'Summer' ? 'bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border-yellow-500/20' :
                        'bg-gradient-to-br from-orange-900/30 to-red-900/30 border-orange-500/20'
                      }`}>
                        <div className="text-2xl mb-2">
                          {season.season === 'Winter' ? '❄️' : season.season === 'Spring' ? '🌸' : season.season === 'Summer' ? '☀️' : '🍂'}
                        </div>
                        <div className="text-white font-bold text-lg mb-2">{season.season}</div>
                        <div className="space-y-1 text-sm">
                          <div className="text-gray-300">{season.hours}hrs • {season.tracks} tracks</div>
                          <div className="text-gray-400">{season.artists} unique artists</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comeback Tracks */}
              {summary.comebackTracks.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-indigo-400" />
                    Comeback Tracks
                  </h3>
                  <p className="text-gray-400 mb-4">Songs you stopped listening to, then rediscovered</p>
                  <div className="space-y-3">
                    {summary.comebackTracks.map((track: any, idx: number) => (
                      <div key={idx} className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg p-4 border border-indigo-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium flex-1 line-clamp-1">{track.track}</span>
                          <span className="text-indigo-400 text-sm ml-3">Gap of {track.maxGap} tracks</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Most Diverse Day */}
              {summary.mostDiverseDay && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    🎨 Most Diverse Listening Day
                  </h3>
                  <div className="bg-gradient-to-br from-pink-900/30 to-purple-900/30 rounded-xl p-6 border border-pink-500/20 mb-6">
                    <div className="text-3xl font-bold text-white mb-2">{new Date(summary.mostDiverseDay.day).toLocaleDateString()}</div>
                    <div className="text-pink-300 text-lg mb-4">Your most eclectic day!</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-400 text-sm mb-2">Artists</div>
                        <div className="text-white font-bold text-2xl mb-2">{summary.mostDiverseDay.artists}</div>
                        <div className="text-sm text-gray-400">tracks</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-2">Tracks</div>
                        <div className="text-white font-bold text-2xl mb-2">{summary.mostDiverseDay.tracks}</div>
                        <div className="text-sm text-gray-400">artists</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-indigo-400" />
                Advanced Analytics
              </h2>

              {/* ENHANCED INSIGHTS - NEW SECTION */}
              <div className="bg-gradient-to-r from-indigo-900/30 via-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-indigo-500/30">
                <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-pink-400 mb-2">
                  🔮 Deep Dive Analytics
                </h2>
                <p className="text-gray-400 mb-6">Advanced insights into your listening behavior</p>
              </div>

              {/* Listening Milestones */}
              {summary.milestones && summary.milestones.length > 0 && (
                <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                  <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                    🎖️ Milestones Achieved
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {summary.milestones.map((milestone: any, idx: number) => (
                      <div key={idx} className={`bg-gradient-to-br ${milestone.gradient} rounded-xl p-6 text-white`}>
                        <h4 className="text-sm font-medium opacity-90 mb-2">{milestone.title}</h4>
                        <p className="text-xl font-bold mb-1 line-clamp-1">{milestone.value}</p>
                        <p className="text-sm opacity-80">{milestone.stat}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Listening Personality Profile */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  🎭 Your Listening Personality
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-xl p-5 border border-blue-500/20">
                    <div className="text-sm text-gray-400 mb-2">Discovery Phase</div>
                    <div className="text-4xl font-bold text-white mb-2">{summary.discoveryPhase}</div>
                    <div className="text-xs text-blue-300">Music exploration level</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-5 border border-purple-500/20">
                    <div className="text-sm text-gray-400 mb-2">Attention Style</div>
                    <div className="text-4xl font-bold text-white mb-2">{summary.attentionType}</div>
                    <div className="text-xs text-purple-300">How long you focus</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl p-5 border border-green-500/20">
                    <div className="text-sm text-gray-400 mb-2">Listening Style</div>
                    <div className="text-4xl font-bold text-white mb-2">{summary.listeningStyle}</div>
                    <div className="text-xs text-green-300">Context variety: {summary.socialScore}/100</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-5 border border-orange-500/20">
                    <div className="text-sm text-gray-400 mb-2">Taste Evolution</div>
                    <div className="text-4xl font-bold text-white mb-2">{summary.evolutionType}</div>
                    <div className="text-xs text-orange-300">Change score: {summary.evolutionScore.toFixed(0)}/100</div>
                  </div>
                </div>
              </div>

              {/* Listening Momentum */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  Listening Momentum
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl p-6 border border-green-500/20">
                    <div className="text-gray-400 text-sm mb-2">Overall Trend</div>
                    <div className="text-4xl font-bold text-white mb-2">{summary.momentumType}</div>
                    <div className="text-green-300 text-lg">
                      {summary.avgMomentumChange > 0 ? '+' : ''}{summary.avgMomentumChange}% per week
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-xl p-6 border border-blue-500/20">
                    <div className="text-gray-400 text-sm mb-2">Recent Weeks</div>
                    <div className="space-y-2">
                      {summary.momentumTrend.slice(-5).reverse().map((week: any, idx: number) => (
                        <div key={idx} className="flex justify-between items-center">
                          <span className="text-gray-300 text-sm">Week {week.week}</span>
                          <span className={`font-bold ${parseFloat(week.change) > 0 ? 'text-green-400' : parseFloat(week.change) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                            {week.change > 0 ? '+' : ''}{week.change}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-900/50 rounded-xl p-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={summary.momentumTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="week" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Area type="monotone" dataKey="hours" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Deep Listening Score */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                  Deep Listening Analysis
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-6 border border-purple-500/20">
                    <div className="text-4xl sm:text-6xl font-bold text-white mb-2">{summary.deepListeningScore}%</div>
                    <div className="text-purple-300 text-lg mb-4">{summary.deepListeningLevel}</div>
                    <p className="text-gray-400 text-sm">
                      {parseFloat(summary.deepListeningScore) > 75 ? 'You fully immerse yourself in music, listening deeply and intentionally.' :
                       parseFloat(summary.deepListeningScore) > 50 ? 'You engage meaningfully with your music, balancing depth and variety.' :
                       parseFloat(summary.deepListeningScore) > 25 ? 'You enjoy music casually, often in the background.' :
                       'You tend to skim through tracks, looking for the perfect moment.'}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="text-white font-semibold mb-3">Engagement Metrics</div>
                    {[
                      { label: 'Track Completion', value: summary.deepListeningMetrics.avgCompletionRate.toFixed(0) },
                      { label: 'Heavy Rotation', value: summary.deepListeningMetrics.heavyRotationPercent.toFixed(1) },
                      { label: 'Session Depth', value: summary.deepListeningMetrics.sessionDepth.toFixed(0) },
                      { label: 'Artist Loyalty', value: summary.deepListeningMetrics.artistLoyalty.toFixed(0) }
                    ].map((metric, idx) => (
                      <div key={idx} className="bg-gray-900/50 rounded-xl p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-400 text-sm">{metric.label}</span>
                          <span className="text-white font-bold">{metric.value}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, parseFloat(metric.value.toString()))}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Artist Network Analysis */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Users className="w-6 h-6 text-blue-400" />
                  Artist Network Map
                </h3>
                
                <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl p-6 border border-blue-500/20 mb-6">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Network Type</div>
                      <div className="text-white font-bold text-xl mb-1">{summary.networkType}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Most Connected</div>
                      <div className="text-white font-bold text-xl mb-1">{summary.mostConnectedArtist?.artist || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-2">Avg Network Size</div>
                      <div className="text-white font-bold text-xl mb-1">{summary.avgNetworkSize} artists</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-white font-semibold mb-3">Top Artist Connections</div>
                  {summary.artistNetworks.slice(0, 5).map((network: any, idx: number) => (
                    <div key={idx} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1">
                          <div className="text-white font-medium truncate">{network.artist}</div>
                          <div className="text-sm text-gray-400">{network.totalConnections} total connections</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-400">{network.networkSize}</div>
                          <div className="text-xs text-gray-400">connected artists</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {network.topPartners.map((partner: any, pIdx: number) => (
                          <div key={pIdx} className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-1 text-blue-300 text-sm">
                            {partner.partner} ({partner.count}×)
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Listening Modes Clustering */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-orange-400" />
                  Listening Modes
                </h3>
                
                <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-6 border border-orange-500/20 mb-6">
                  <div className="text-4xl font-bold text-white mb-3">{summary.listeningMode}</div>
                  <div className="text-orange-300 text-lg mb-4">Your dominant listening pattern</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(summary.modeDistribution).map(([mode, count]: [string, any], idx) => (
                    <div key={idx} className="bg-gray-900/50 rounded-xl p-4 text-center border border-gray-700/50">
                      <div className="text-3xl font-bold text-white mb-1">{count}</div>
                      <div className="text-gray-400 text-sm capitalize">{mode} Sessions</div>
                      <div className="text-xs text-gray-500 mt-2">
                        {mode === 'focused' ? '60+ min, <5 artists' :
                         mode === 'explore' ? '30+ min, 10+ artists' :
                         mode === 'quick' ? '<30 min' : '120+ min'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Track Completion Intelligence */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Play className="w-6 h-6 text-green-400" />
                  Track Completion Intelligence
                </h3>
                
                <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-xl p-6 border border-green-500/20 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white mb-3">{summary.overallCompletionScore}%</div>
                    <div className="text-green-300 text-lg mb-4">{summary.completionPersonality}</div>
                    <p className="text-gray-400 text-sm">
                      {parseFloat(summary.overallCompletionScore) > 80 ? 'You rarely skip tracks, showing patience and appreciation for full songs.' :
                       parseFloat(summary.overallCompletionScore) > 60 ? 'You complete most tracks you start, being selective about what you play.' :
                       'You frequently skip tracks, always searching for the perfect moment.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-white font-semibold mb-3">Top Completed Tracks</div>
                  {summary.trackCompletionScores.slice(0, 8).map((track: any, idx: number) => (
                    <div key={idx} className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/50">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{track.track}</div>
                          <div className="text-gray-400 text-sm">{track.plays} plays</div>
                        </div>
                        <div className="text-green-400 font-bold text-lg ml-4">{track.completionRate}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendation Affinity */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  Discovery Openness
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 rounded-xl p-6 border border-yellow-500/20">
                    <div className="text-4xl font-bold text-white mb-2">{summary.recommendationAffinity.personality}</div>
                    <div className="text-yellow-300 text-lg mb-4">Your discovery profile</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-400 text-sm">Openness</div>
                        <div className="text-white font-bold text-xl mb-2">{summary.recommendationAffinity.openness}%</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm">Trend</div>
                        <div className="text-white font-bold text-xl mb-2">{summary.recommendationAffinity.trend}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col justify-center">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">New artists/month</span>
                        <span className="text-white font-bold text-xl">{summary.recommendationAffinity.avgNewPerMonth}</span>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {parseFloat(summary.recommendationAffinity.openness) > 15 ? 'You actively seek out new artists and thrive on discovery!' :
                         parseFloat(summary.recommendationAffinity.openness) > 8 ? 'You balance familiar favorites with new discoveries.' :
                         parseFloat(summary.recommendationAffinity.openness) > 3 ? 'You prefer deepening relationships with known artists.' :
                         'You stick with what you know and love.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Listening Fragmentation */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-pink-400" />
                  Listening Fragmentation
                </h3>
                
                <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/30 rounded-xl p-6 border border-pink-500/20 mb-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-3xl sm:text-5xl font-bold text-white mb-2">{summary.fragmentationScore}%</div>
                      <div className="text-pink-300 text-lg mb-4">{summary.fragmentationType}</div>
                      <p className="text-gray-400 text-sm">
                        {parseFloat(summary.fragmentationScore) > 70 ? 'Your listening is focused and consistent.' :
                         parseFloat(summary.fragmentationScore) > 40 ? 'You balance focus with variety nicely.' :
                         parseFloat(summary.fragmentationScore) > 20 ? 'Your listening is quite scattered and exploratory.' :
                         'You flit between music like a butterfly!'}
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      {[
                        { label: 'Single Play Rate', value: summary.fragmentationMetrics.singlePlayRate.toFixed(0), inverse: true },
                        { label: 'Artist Focus', value: (100 - summary.fragmentationMetrics.artistConcentration).toFixed(0), inverse: false },
                        { label: 'Session Consistency', value: summary.fragmentationMetrics.sessionConsistency },
                        { label: 'Mode Variety', value: summary.fragmentationMetrics.modeVariety * 25 }
                      ].map((metric, idx) => (
                        <div key={idx} className="bg-gray-900/50 rounded-xl p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-400 text-sm">{metric.label}</span>
                            <span className="text-white font-bold">{metric.value}%</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-pink-500 to-rose-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, parseFloat(metric.value.toString()))}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Trend Forecasting */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-cyan-400" />
                  Future Trends Forecast
                </h3>
                
                <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-xl p-6 border border-cyan-500/20">
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-white mb-3">{summary.trendForecast.prediction}</div>
                    <div className="text-cyan-300 text-lg">Predicted listening trajectory</div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="text-gray-400 text-sm mb-2">Listening Volume</div>
                      <div className="text-white font-bold text-2xl mb-2">{summary.trendForecast.listeningVolume}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="text-gray-400 text-sm mb-2">Discovery Rate</div>
                      <div className="text-white font-bold text-2xl mb-2">{summary.trendForecast.discoveryRate}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personality Trait Breakdown */}
              <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-amber-400" />
                  Personality Trait Breakdown
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(summary.personalityTraits).map(([trait, score]: [string, any], idx) => (
                    <div key={idx} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-semibold capitalize">{trait}</span>
                        <span className="text-white font-bold">{Math.min(100, score).toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <div className={`h-3 rounded-full transition-all duration-300 ${idx % 4 === 0 ? 'bg-gradient-to-r from-purple-500 to-pink-500' : idx % 4 === 1 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : idx % 4 === 2 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-orange-500 to-red-500'}`} style={{ width: `${Math.min(100, score)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/20">
                  <p className="text-gray-300 text-center">
                    Your unique combination of traits creates a one-of-a-kind musical personality. 
                    These patterns reveal not just what you listen to, but how you experience music.
                  </p>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    );
  };

  return summary ? <ResultsView /> : <UploadView />;
}

export default App;
